/*!
 * DIREVULF — Fandom Hub (assets/fandom-hub.js)
 * Client-side fandom discovery hub. Reads the content layer (fandom-hub*.json),
 * optionally enriches it from open sources (Wikipedia, Wikidata, Wikimedia Commons,
 * a Fandom wiki, ComicNewbies headlines) with caching and graceful fallback.
 * Scope: everything lives under [data-dvh]; no global listeners except popstate,
 * keydown (only while a layer is open) and one IntersectionObserver.
 */
(function () {
  'use strict';
  var root = document.querySelector('[data-dvh]');
  if (!root || root.__dvh) return;
  root.__dvh = true;

  var CFG = {};
  try { CFG = JSON.parse(root.querySelector('[data-dvh-config]').textContent); } catch (e) { CFG = {}; }
  var LIVE = CFG.live || {};
  var UA = { 'Api-User-Agent': 'DIREVULF-FandomHub/1.0 (https://direvulf.com)' };
  var DAY = 864e5, HOUR = 36e5;

  /* ------------------------------------------------------------------ helpers */
  function $(s, c) { return (c || root).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || root).querySelectorAll(s)); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function decodeEntities(s) { var t = document.createElement('textarea'); t.innerHTML = String(s || ''); return t.value; }
  function words(s) { return String(s || '').trim().split(/\s+/).filter(Boolean).length; }
  var DIACRITICS = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');
  function norm(s) { return String(s || '').toLowerCase().normalize('NFD').replace(DIACRITICS, '').replace(/[^a-z0-9]+/g, ' ').trim(); }
  function fmtDate(d, withYear) {
    if (!(d instanceof Date) || isNaN(d)) return '';
    return d.toLocaleDateString('en-GB', withYear === false ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  }
  function initials(n) { return String(n || '').replace(/\(.*?\)/g, '').split(/[\s\-]+/).filter(Boolean).slice(0, 2).map(function (w) { return w.charAt(0); }).join('').toUpperCase(); }
  function ext(url, label, cls) { return '<a class="' + (cls || 'dvh-link') + '" href="' + esc(url) + '" target="_blank" rel="noopener">' + label + '</a>'; }

  var store = {
    get: function (k) { try { var r = JSON.parse(localStorage.getItem('dvh:' + k)); return r && r.t ? r : null; } catch (e) { return null; } },
    set: function (k, v) {
      var rec = JSON.stringify({ t: Date.now(), v: v });
      try { localStorage.setItem('dvh:' + k, rec); }
      catch (e) { try { Object.keys(localStorage).forEach(function (x) { if (x.indexOf('dvh:') === 0 && x.indexOf('dvh:f1news') !== 0) localStorage.removeItem(x); }); localStorage.setItem('dvh:' + k, rec); } catch (e2) {} }
    }
  };

  function getJSON(url, opt) {
    opt = opt || {};
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, opt.timeout || 9000);
    return fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: opt.headers || {}, credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .finally(function () { clearTimeout(timer); });
  }

  /* Small queue so the page never sends more than two requests at a time to Wikimedia. */
  var queue = [], active = 0;
  function queued(fn) {
    return new Promise(function (res, rej) {
      queue.push(function () { active++; fn().then(res, rej).finally(function () { active--; setTimeout(pump, 120); }); });
      pump();
    });
  }
  function pump() { while (active < 2 && queue.length) queue.shift()(); }

  /* cached(key, ttl, loader) -> {v, t, live, stale}. Fresh cache wins; otherwise try the
     loader; on failure fall back to any cached copy (stale) or reject. */
  var inflight = {};
  function cached(key, ttl, loader) {
    var c = store.get(key);
    if (c && Date.now() - c.t < ttl) return Promise.resolve({ v: c.v, t: c.t, live: false, stale: false });
    if (inflight[key]) return inflight[key];
    var p = loader().then(function (v) {
      store.set(key, v); delete inflight[key];
      return { v: v, t: Date.now(), live: true, stale: false };
    }, function (err) {
      delete inflight[key];
      if (c) return { v: c.v, t: c.t, live: false, stale: true };
      throw err;
    });
    inflight[key] = p;
    return p;
  }

  /* ------------------------------------------------------------------ content model */
  var CORE = null, PARTS = {}, STATE = { fandom: null, franchise: 'all', blogCat: 'all' };
  function fandomCfg(k) { return (CORE.fandoms || []).filter(function (f) { return f.key === k; })[0]; }
  function loadCore() {
    return getJSON(CFG.content, { timeout: 12000 }).then(function (d) { CORE = d; return d; });
  }
  function loadPart(k) {
    if (PARTS[k]) return PARTS[k].promise;
    var url = (CFG.parts || {})[k];
    var p = url ? getJSON(url, { timeout: 12000 }) : Promise.reject(new Error('no content file for ' + k));
    PARTS[k] = { promise: p.then(function (d) {
      PARTS[k].data = d;
      d.byId = {};
      // image lists come from the core file (item_images / blog_images); every item that owns an
      // image is listed there explicitly
      var II = (CORE && CORE.item_images) || {}, BI = (CORE && CORE.blog_images) || {};
      (d.items || []).forEach(function (i) { i.fandom = k; d.byId[i.id] = i; if (II[i.id]) i.img = II[i.id].slice(); });
      (d.blogs || []).forEach(function (b) { d.byId[b.id] = b; var x = BI[b.id]; if (x) { b.hero = x.hero; b.gallery = x.gallery || []; b.inline = x.inline || {}; } });
      return d;
    }) };
    return PARTS[k].promise;
  }
  function part(k) { return PARTS[k] && PARTS[k].data; }

  /* Source codes -> full attribution. */
  function source(code) {
    code = code || 'dv';
    var S = (CORE && CORE.sources) || {};
    if (code === 'dv') return { name: 'DIREVULF', url: null, type: 'editorial', license: 'DIREVULF editorial', attribution: 'Written by DIREVULF.' };
    var bits = code.split(':'), kind = bits[0];
    if (kind === 'wp') {
      var t = code.slice(3), n = t.replace(/_/g, ' ');
      return { name: 'Wikipedia', title: n, url: 'https://en.wikipedia.org/wiki/' + encodeURIComponent(t).replace(/%2F/g, '/').replace(/%3A/g, ':').replace(/%2C/g, ','), type: S.wp && S.wp.type, license: 'CC BY-SA 4.0', licenseUrl: S.wp && S.wp.license_url, api: 'wp', key: t };
    }
    if (kind === 'fd') {
      var w = bits[1], title = bits.slice(2).join(':'), wn = (CORE.wikis || {})[w] || (w + ' wiki');
      return { name: wn + ' (Fandom)', title: title, url: 'https://' + w + '.fandom.com/wiki/' + encodeURIComponent(title.replace(/ /g, '_')), type: 'community wiki', license: 'CC BY-SA 3.0', licenseUrl: 'https://www.fandom.com/licensing', api: 'fd', wiki: w, key: title };
    }
    return { name: code, url: null, license: '' };
  }
  function srcLine(code, label) {
    var s = source(code);
    if (!s.url) return '<p class="dvh-src">Source: DIREVULF editorial</p>';
    return '<p class="dvh-src">' + (label || 'Source') + ': <a href="' + esc(s.url) + '" target="_blank" rel="noopener">' + esc(s.name) + (s.title ? ' — ' + esc(s.title) : '') + '</a> <span class="dvh-src__lic">' + esc(s.license) + '</span></p>';
  }

  /* ------------------------------------------------------------------ images
     Data model (content layer "images"): id -> {f file, l licence, a author, w px width,
     r width/height, cap caption, t type, p Commons hash path, o use original}. An item lists
     its images in order ("img"); a blog has "hero", "gallery" and "inline" {section: id}.
     Each rendered image resolves to: image_url, thumbnail_url, source_image_url, image_alt,
     source_name, source_url, license, attribution, image_type.
     Fallback chain for every <img>: requested thumbnail -> original file -> next image in the
     item's list (thumbnail, then original) -> designed DIREVULF fallback card. Each URL is
     tried at most once per page view; a URL that failed on two views within 6 h is skipped, and
     the browser cache keeps the ones that loaded. No broken image element is left in the DOM. */
  var COMMONS = 'https://upload.wikimedia.org/wikipedia/commons/';
  var STEPS = [330, 500, 960, 1280];
  // Failed URLs: {url: {n: failures, t: last}}. A URL is skipped only after failing on two page
  // views within 6 h, so one flaky connection never hides an image for long.
  var BADSEEN = {};
  var IMGBAD = (function () { var m = store.get('imgbad'), v = (m && m.v) || {}, now = Date.now(); Object.keys(v).forEach(function (u) { if (!v[u] || now - v[u].t > 6 * HOUR) delete v[u]; }); return v; })();
  var badTimer = null;
  function isBad(u) { return !!(BADSEEN[u] || (IMGBAD[u] && IMGBAD[u].n >= 2)); }
  function markBad(u) { if (!u || BADSEEN[u]) return; BADSEEN[u] = 1; var r = IMGBAD[u] || { n: 0 }; IMGBAD[u] = { n: r.n + 1, t: Date.now() }; clearTimeout(badTimer); badTimer = setTimeout(function () { store.set('imgbad', IMGBAD); }, 600); }
  function imgRec(id) {
    var im = CORE && CORE.images && CORE.images[id];
    if (!im) return null;
    var name = encodeURIComponent(im.f.replace(/ /g, '_')).replace(/%2C/g, ',');
    var svg = /\.svg$/i.test(im.f), orig = COMMONS + im.p + '/' + name;
    function thumb(w) {
      if (!svg && (im.o || (im.w && im.w <= w))) return orig;
      return COMMONS + 'thumb/' + im.p + '/' + name + '/' + w + 'px-' + name + (svg ? '.png' : '');
    }
    var widths = STEPS.filter(function (w) { return svg || (!im.o && (!im.w || im.w > w)); });
    return {
      id: id, thumb: thumb, widths: widths,
      image_url: thumb(960), thumbnail_url: thumb(330), source_image_url: orig,
      image_alt: im.alt || im.cap || '', caption: im.cap || '', image_type: im.t || 'photo',
      source_name: 'Wikimedia Commons', source_url: 'https://commons.wikimedia.org/wiki/File:' + encodeURIComponent(im.f.replace(/ /g, '_')),
      license: im.l, licenseUrl: (CORE.licenses || {})[im.l] || null, attribution: im.a || '',
      ratio: im.r || 1.333, width: im.w || 0,
      image_id: id, source: 'Wikimedia Commons', height: im.h || 0, aspect_ratio: im.r || 1.333,
      fandom: im.fd || '', character: im.ch || [], topic: im.ct || [], content_id: im.fd && (im.ch || im.ct) ? im.fd + '-' + ((im.ch || im.ct)[0]) : ''
    };
  }
  /* ------------------------------------------------------------------ image allocation
     ONE IMAGE -> ONE CONTENT ITEM. Every card, panel/article figure and mini card asks an
     allocator for its image. Candidates are tiered, most specific first:
       T1 the item's own images (content-specific; for a character or blog, its own pool),
       T2 images chosen for the same story/topic (content ids),
       T3 for a story/event/moment/note/team: an image of a character it is about, whose
          credit line says what the image actually shows (its Commons caption).
     Nothing else is ever used — no other character, no other fandom, no random pick. An image
     already shown in the same view (the page, or the open panel/article) is not picked again;
     when no unused relevant image is left, the designed text card is shown. The page and the
     layer keep separate usage maps keyed by the normalised Commons file name. */
  var IDX = null;
  function imgIndex() {
    if (IDX) return IDX;
    IDX = { ch: {}, ct: {} };
    Object.keys((CORE && CORE.images) || {}).forEach(function (id) {
      var im = CORE.images[id];
      (im.ch || []).forEach(function (c) { (IDX.ch[c] = IDX.ch[c] || []).push(id); });
      (im.ct || []).forEach(function (c) { (IDX.ct[c] = IDX.ct[c] || []).push(id); });
    });
    return IDX;
  }
  function imgKey(id) { var im = CORE && CORE.images && CORE.images[id]; return im ? im.f.toLowerCase().replace(/[\s_]+/g, '_') : String(id || ''); }
  function recBad(id) { var r = imgRec(id); return !r || (isBad(r.thumb(500)) && isBad(r.source_image_url)); }
  function ownImgs(it) {
    var l = it && it.img ? it.img.slice() : [];
    if (it && it.id) (imgIndex().ch[it.id] || []).forEach(function (x) { if (l.indexOf(x) < 0) l.push(x); });
    return l;
  }
  var STORY_TYPES = ['story', 'event', 'moment', 'note', 'team'], PERSON_TYPES = ['character', 'driver', 'dragon', 'team'];
  function itemCands(it, d) {
    var out = [], seen = {};
    function add(id, tier, pic) { if (id && !seen[id] && imgRec(id)) { seen[id] = 1; out.push({ id: id, t: tier, p: pic || '' }); } }
    if (!it) return out;
    ownImgs(it).forEach(function (x) { add(x, 1); });
    (imgIndex().ct[it.id] || []).forEach(function (x) { add(x, 2); });
    if (d && STORY_TYPES.indexOf(it.type) > -1) (it.rel || []).forEach(function (r) {
      var c = d.byId[r];
      if (c && PERSON_TYPES.indexOf(c.type) > -1) ownImgs(c).forEach(function (x) { add(x, 3, c.name); });
    });
    return out;
  }
  function blogCands(b, d) {
    var out = [], seen = {};
    function add(id) { if (id && !seen[id] && imgRec(id)) { seen[id] = 1; out.push({ id: id, t: 1, p: '' }); } }
    add(b.hero); (b.gallery || []).forEach(add);
    Object.keys(b.inline || {}).forEach(function (k) { add(b.inline[k]); });
    (imgIndex().ct[b.id] || []).forEach(add);
    ownImgs(d.byId[b.char]).forEach(add);
    return out;
  }
  function Alloc(name) { this.name = name; this.used = {}; }
  Alloc.prototype.free = function (id) { return !this.used[imgKey(id)]; };
  Alloc.prototype.take = function (id, owner, sec) { var k = imgKey(id), u = this.used[k]; if (u) { u.n++; u.owners.push(owner); } else this.used[k] = { id: id, n: 1, owners: [owner], sec: sec }; };
  Alloc.prototype.release = function (id, owner) { var k = imgKey(id), u = this.used[k]; if (!u) return; var i = u.owners.indexOf(owner); if (i > -1) u.owners.splice(i, 1); u.n = u.owners.length; if (!u.n) delete this.used[k]; };
  Alloc.prototype.releaseSec = function (sec) { var U = this.used; Object.keys(U).forEach(function (k) { if (U[k].sec === sec) delete U[k]; }); };
  Alloc.prototype.pick = function (cands, owner, sec) {
    for (var i = 0; i < cands.length; i++) {
      var c = cands[i];
      if (this.free(c.id) && !recBad(c.id)) {
        this.take(c.id, owner, sec);
        return { id: c.id, t: c.t, p: c.p, alts: cands.slice(i + 1).map(function (x) { return { id: x.id, p: x.p, t: x.t }; }) };
      }
    }
    return null;
  };
  Alloc.prototype.all = function (cands, owner, sec, max) {
    var self = this, out = [];
    cands.forEach(function (c) { if ((!max || out.length < max) && self.free(c.id) && !recBad(c.id)) { self.take(c.id, owner, sec); out.push(c.id); } });
    return out;
  };
  var PAGE = new Alloc('page'), LAYER = new Alloc('layer'), PLAN = {};
  function allocFor(name) { return name === 'layer' ? LAYER : PAGE; }
  function cid(x) { return (STATE.fandom || '') + '-' + ((x && x.id) || ''); }
  /* -> slot {cid, sec, pool, id|null, t tier, p pictured name, alts} */
  function pickSlot(A, cands, owner, sec) {
    var r = A.pick(cands, owner, sec);
    return { cid: owner, sec: sec, pool: A.name, id: r ? r.id : null, t: r ? r.t : 0, p: r ? r.p : '', alts: r ? r.alts : [] };
  }
  function slotIds(sl) { return sl && sl.id ? [sl.id] : []; }
  function slotAttrs(sl) { return sl ? ' data-cid="' + esc(sl.cid) + '" data-sec="' + esc(sl.sec) + '" data-pool="' + esc(sl.pool || 'page') + '" data-tier="' + (sl.t || 0) + '"' : ''; }
  /* page plan: blogs first, then character profiles, then the story sections in page order */
  function slotFor(sec, it, d, kind) {
    var key = sec + ':' + it.id;
    if (!PLAN[key]) PLAN[key] = pickSlot(PAGE, kind === 'blog' ? blogCands(it, d) : itemCands(it, d), cid(it), sec);
    return PLAN[key];
  }
  /* Page plan, in tiers: first every visible item gets one of its OWN images (blogs, then
     character profiles, then the story sections in page order); then images chosen for the same
     topic; only then may a story borrow an unused image of a character it is about — the most
     specific stories (fewest characters) first. Whatever is left shows the designed text card. */
  function planPage(f, d) {
    PAGE = new Alloc('page'); PLAN = {};
    var secs = visibleSections(f, d).map(function (x) { return x.type; }), slots = [];
    function add(sec, it, kind) { slots.push({ key: sec + ':' + it.id, sec: sec, it: it, c: kind === 'blog' ? blogCands(it, d) : itemCands(it, d) }); }
    if (secs.indexOf('blogs') > -1) blogsShown(d).shown.forEach(function (b) { add('blogs', b, 'blog'); });
    if (secs.indexOf('characters') > -1) itemsOf(d, 'character').concat(itemsOf(d, 'driver')).forEach(function (it) { add('characters', it); });
    secs.forEach(function (t) {
      if (['shop', 'blogs', 'characters', 'products', 'fact', 'timeline', 'featured'].indexOf(t) > -1 || t.indexOf('f1-') === 0) return;
      itemsOf(d, t).forEach(function (it) { add(t, it); });
    });
    [1, 2, 3].forEach(function (tier) {
      var open = slots.filter(function (x) { return !PLAN[x.key]; });
      if (tier === 3) {
        var spread = function (x) { return (x.it.rel || []).length || 99; };
        open = open.map(function (x, i) { return { x: x, i: i, n: spread(x) }; }).sort(function (a, b) { return a.n - b.n || a.i - b.i; }).map(function (o) { return o.x; });
      }
      open.forEach(function (x) {
        var r = PAGE.pick(x.c.filter(function (c) { return c.t === tier; }), cid(x.it), x.sec);
        if (r) PLAN[x.key] = { cid: cid(x.it), sec: x.sec, pool: 'page', id: r.id, t: r.t, p: r.p, alts: x.c.filter(function (c) { return c.id !== r.id; }).map(function (c) { return { id: c.id, p: c.p, t: c.t }; }) };
      });
    });
    slots.forEach(function (x) { if (!PLAN[x.key]) PLAN[x.key] = { cid: cid(x.it), sec: x.sec, pool: 'page', id: null, t: 0, p: '', alts: [] }; });
  }

  function hashN(s) { var h = 0; s = String(s || ''); for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; }
  /* Designed fallback card: shown only when no licensed image exists (or every URL failed). */
  function fallbackCard(m, size, attrs) {
    m = m || {}; var h = hashN(m.n);
    return '<div class="dvh-fb' + (size ? ' dvh-fb--' + size : '') + '"' + (attrs || '') + ' style="--dvh-fb-a:' + (100 + h % 70) + 'deg;--dvh-fb-x:' + (15 + h % 70) + '%;--dvh-fb-y:' + (h % 40) + '%" role="img" aria-label="' + esc((m.n || '') + (m.c ? ' — ' + m.c : '') + (m.f ? ', ' + m.f : '')) + '">' +
      '<span class="dvh-fb__mono" aria-hidden="true">' + esc(initials(m.n)) + '</span>' +
      '<span class="dvh-fb__meta" aria-hidden="true">' + (m.c ? '<span class="dvh-fb__cat">' + esc(m.c) + '</span>' : '') + '<span class="dvh-fb__name">' + esc(m.n || '') + '</span>' +
      '<span class="dvh-fb__src">' + esc(m.f || '') + (m.s ? ' · ' + esc(m.s) : '') + '</span></span></div>';
  }
  function tile(label, kind, big) { return fallbackCard({ n: label, c: kind, f: (STATE.fandom && fandomCfg(STATE.fandom) || {}).name }, big ? 'big' : ''); }
  function fbMeta(name, cat, srcCode) { var f = STATE.fandom && fandomCfg(STATE.fandom); var s = srcCode ? source(srcCode) : null; return { n: name, c: cat, f: f ? f.name : '', s: s && s.url ? 'Source: ' + s.name : '' }; }
  /* <img> for an ordered list of image ids, or the fallback card if none resolve. */
  function picHTML(ids, meta, opts) {
    opts = opts || {};
    var recs = (ids || []).map(imgRec).filter(Boolean);
    var want = opts.want || 500, chain = [];
    recs.forEach(function (r) { [r.thumb(want), r.source_image_url].forEach(function (u) { if (!isBad(u) && !chain.some(function (c) { return c.u === u; })) chain.push({ u: u, id: r.id }); }); });
    if (!chain.length) return fallbackCard(meta, opts.fb, slotAttrs(opts.slot));
    var r0 = imgRec(chain[0].id), useSet = chain[0].u === r0.thumb(want) && r0.widths.length > 1;
    var srcset = useSet ? r0.widths.map(function (w) { return r0.thumb(w) + ' ' + w + 'w'; }).join(', ') : '';
    var w = 640, h = Math.round(640 / (r0.ratio || 1.333));
    return '<img class="dvh-img" src="' + esc(chain[0].u) + '"' + (srcset ? ' srcset="' + esc(srcset) + '" sizes="' + esc(opts.sizes || '(max-width: 640px) 80vw, 330px') + '"' : '') +
      ' width="' + w + '" height="' + h + '" alt="' + esc(opts.alt != null ? opts.alt : (r0.image_alt || meta.n || '')) + '" loading="' + (opts.eager ? 'eager' : 'lazy') + '" decoding="async"' + (opts.eager ? ' fetchpriority="high"' : '') +
      ' data-dvh-img data-chain="' + esc(JSON.stringify(chain)) + '" data-i="0" data-cur="' + esc(chain[0].id) + '"' +
      ' data-fb="' + esc(JSON.stringify(meta || {})) + '" data-fbsize="' + esc(opts.fb || '') + '" data-want="' + want + '"' + slotAttrs(opts.slot) +
      (opts.slot && opts.slot.alts && opts.slot.alts.length ? ' data-alt="' + esc(JSON.stringify(opts.slot.alts.slice(0, 8))) + '"' : '') + '>';
  }
  function creditInner(id, pictured) {
    var r = imgRec(id); if (!r) return '';
    // an image borrowed from a related character says what it actually shows (its caption)
    return (pictured ? (r.caption ? esc(r.caption) : 'Related: ' + esc(pictured)) + ' · ' : '') + 'Image: <a href="' + esc(r.source_url) + '" target="_blank" rel="noopener">' + esc(r.attribution || 'Wikimedia Commons') + '</a> · ' +
      (r.licenseUrl ? '<a href="' + esc(r.licenseUrl) + '" target="_blank" rel="noopener">' + esc(r.license) + '</a>' : esc(r.license)) + ' · Wikimedia Commons';
  }
  function creditHTML(ids, pictured) {
    var id = (ids || []).filter(function (x) { return imgRec(x); })[0];
    return id ? '<p class="dvh-credit" data-dvh-credit data-pictured="' + esc(pictured || '') + '">' + creditInner(id, pictured) + '</p>' : '';
  }
  /* Figure with caption + credit (article hero/inline/gallery). */
  function figureHTML(id, meta, opts) {
    opts = opts || {};
    var r = imgRec(id); if (!r) return '';
    return '<figure class="dvh-fig' + (opts.cls ? ' ' + opts.cls : '') + '" data-dvh-fig><div class="dvh-fig__media" style="--dvh-ratio:' + (r.ratio || 1.333) + '">' + picHTML([id], meta, { want: opts.want || 960, sizes: opts.sizes || '(max-width: 640px) 100vw, 860px', eager: opts.eager, alt: r.image_alt, fb: 'big', slot: opts.slot }) + '</div>' +
      '<figcaption class="dvh-fig__cap">' + (r.caption ? '<span>' + esc(r.caption) + '</span> ' : '') + '<span class="dvh-credit dvh-credit--inline" data-dvh-credit>' + creditInner(id) + '</span></figcaption></figure>';
  }
  /* Gallery: one swipeable track (phones) + thumbnails (desktop). Never wider than its column. */
  function galleryHTML(ids, name, title, slot) {
    var recs = (ids || []).map(imgRec).filter(Boolean);
    if (recs.length === 1) return '<section class="dvh-block dvh-gal dvh-gal--one"><h2 class="dvh-h3">' + esc(title) + '</h2>' + figureHTML(recs[0].id, fbMeta(name, 'Image', ''), { slot: slot }) + '</section>';
    if (recs.length < 2) return '';
    return '<section class="dvh-block dvh-gal" data-dvh-gal><h2 class="dvh-h3">' + esc(title) + ' <span class="dvh-gal__n">' + recs.length + ' images</span></h2>' +
      '<div class="dvh-gal__track" tabindex="0" role="region" aria-label="' + esc(name) + ' — images">' + recs.map(function (r) {
        return '<figure class="dvh-gal__slide" data-dvh-fig><div class="dvh-gal__media">' + picHTML([r.id], fbMeta(name, 'Image', ''), { want: 960, sizes: '(max-width: 640px) 92vw, 800px', alt: r.image_alt, fb: 'big', slot: slot }) + '</div>' +
          '<figcaption class="dvh-fig__cap">' + (r.caption ? '<span>' + esc(r.caption) + '</span> ' : '') + '<span class="dvh-credit dvh-credit--inline" data-dvh-credit>' + creditInner(r.id) + '</span></figcaption></figure>';
      }).join('') + '</div>' +
      '<div class="dvh-gal__thumbs" role="group" aria-label="Choose an image">' + recs.map(function (r, i) {
        return '<button type="button" class="dvh-gal__thumb' + (i ? '' : ' is-active') + '" data-dvh-gal-go="' + i + '" aria-label="Show image ' + (i + 1) + ' of ' + recs.length + '"' + (i ? '' : ' aria-current="true"') + '>' + picHTML([r.id], fbMeta(name, '', ''), { want: 330, sizes: '96px', alt: '', fb: 'mini' }).replace('<img ', '<img data-nav ') + '</button>';
      }).join('') + '</div></section>';
  }
  function galActive(gal, i) { $$('.dvh-gal__thumb', gal).forEach(function (b, j) { b.classList.toggle('is-active', i === j); if (i === j) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current'); }); }
  var galTimer = null;
  root.addEventListener('scroll', function (e) {
    var tr = e.target; if (!tr || !tr.classList || !tr.classList.contains('dvh-gal__track')) return;
    clearTimeout(galTimer); galTimer = setTimeout(function () { galActive(tr.closest('[data-dvh-gal]'), Math.round(tr.scrollLeft / Math.max(1, tr.clientWidth))); }, 80);
  }, true);

  root.addEventListener('load', function (e) { var t = e.target; if (t && t.tagName === 'IMG' && t.hasAttribute('data-dvh-img')) t.setAttribute('data-ok', '1'); }, true);
  /* One error handler walks the chain; when it runs out the fallback card replaces the <img>. */
  root.addEventListener('error', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'IMG') return;
    if (t.parentNode && t.parentNode.hasAttribute && t.parentNode.hasAttribute('data-dvh-collage')) {
      markBad(t.currentSrc || t.src);
      var wasOn = t.classList.contains('is-on'), host = t.parentNode; t.remove();
      var nxt = host.querySelector('img'); if (wasOn && nxt) nxt.classList.add('is-on');
      var cr = root.querySelector('[data-dvh-herocredit]'), on = host.querySelector('img.is-on');
      if (cr) cr.innerHTML = on ? 'Image: <a href="' + esc(on.getAttribute('data-page')) + '" target="_blank" rel="noopener">' + esc(on.getAttribute('data-credit')) + '</a>' : '';
      return;
    }
    if (!t.hasAttribute('data-dvh-img')) return;
    markBad(t.currentSrc || t.src);
    var chain = []; try { chain = JSON.parse(t.getAttribute('data-chain') || '[]'); } catch (x) {}
    var i = +t.getAttribute('data-i') + 1;
    while (i < chain.length && isBad(chain[i].u)) i++;
    var box = t.closest('[data-dvh-fig], .dvh-card, .dvh-panel__media, .dvh-article__hero, .dvh-mini, .dvh-moment, .dvh-gal__slide, .dvh-gal__thumb') || t.parentNode;
    var cred = box && (box.querySelector('[data-dvh-credit]') || (box.nextElementSibling && box.nextElementSibling.hasAttribute && box.nextElementSibling.hasAttribute('data-dvh-credit') ? box.nextElementSibling : null));
    if (i < chain.length) {
      t.removeAttribute('srcset'); t.removeAttribute('sizes');
      if (chain[i].id !== t.getAttribute('data-cur') && cred) cred.innerHTML = creditInner(chain[i].id, cred.getAttribute('data-pictured'));
      t.setAttribute('data-i', String(i)); t.setAttribute('data-cur', chain[i].id); t.src = chain[i].u;
      return;
    }
    // every URL of this image failed: try the next candidate that is still unused in this view
    var alts = []; try { alts = JSON.parse(t.getAttribute('data-alt') || '[]'); } catch (x) {}
    var A = allocFor(t.getAttribute('data-pool')), owner = t.getAttribute('data-cid'), osec = t.getAttribute('data-sec'), want = +t.getAttribute('data-want') || 500;
    if (owner) A.release(t.getAttribute('data-cur'), owner);
    for (var j = 0; j < alts.length; j++) {
      var a = alts[j], ar = imgRec(a.id);
      if (!ar || !A.free(a.id) || recBad(a.id)) continue;
      A.take(a.id, owner, osec);
      var nc = [{ u: ar.thumb(want), id: a.id }, { u: ar.source_image_url, id: a.id }].filter(function (c) { return !isBad(c.u); });
      if (!nc.length) continue;
      t.setAttribute('data-chain', JSON.stringify(nc)); t.setAttribute('data-alt', JSON.stringify(alts.slice(j + 1)));
      t.setAttribute('data-i', '0'); t.setAttribute('data-cur', a.id); t.setAttribute('data-tier', String(a.t || 0));
      if (cred) { cred.setAttribute('data-pictured', a.p || ''); cred.innerHTML = creditInner(a.id, a.p); }
      t.src = nc[0].u;
      return;
    }
    var meta = {}; try { meta = JSON.parse(t.getAttribute('data-fb') || '{}'); } catch (x) {}
    var dv = document.createElement('div'); dv.innerHTML = fallbackCard(meta, t.getAttribute('data-fbsize'), owner ? ' data-cid="' + esc(owner) + '" data-sec="' + esc(osec) + '" data-pool="' + esc(t.getAttribute('data-pool') || 'page') + '" data-failed="1"' : '');
    if (cred) cred.remove();
    if (t.parentNode) t.parentNode.replaceChild(dv.firstChild, t);
  }, true);

  /* ------------------------------------------------------------------ cards */
  var TYPE_LABEL = { alien: 'Alien', tech: 'Tech', comic: 'Comic', character: 'Character', team: 'Team', story: 'Story', event: 'Event', moment: 'Moment', note: 'DIREVULF take', dragon: 'Dragon', location: 'Place', car: 'Car', circuit: 'Circuit', driver: 'Driver', timeline: 'Timeline', fact: 'Fact' };

  function relatedCount(it, d) {
    var n = 0;
    (d.items || []).forEach(function (x) { if (x !== it && (x.rel || []).indexOf(it.id) > -1) n++; });
    n += (it.rel || []).length;
    n += (d.blogs || []).filter(function (b) { return b.char === it.id; }).length;
    return n;
  }
  function blogFor(id, d) { return (d.blogs || []).filter(function (b) { return b.char === id; })[0]; }

  function charCard(it, d, f) {
    var n = relatedCount(it, d), blog = blogFor(it.id, d), sl = slotFor('characters', it, d);
    return '<article class="dvh-card dvh-card--char dvh-reveal" data-dvh-open="item" data-id="' + esc(it.id) + '" tabindex="0" role="button" aria-label="' + esc(it.name) + ' — open details">' +
      '<div class="dvh-card__media">' + picHTML(slotIds(sl), fbMeta(it.name, TYPE_LABEL[it.type], it.src), { alt: it.name, sizes: '(max-width: 640px) 64vw, 260px', slot: sl }) + '<span class="dvh-tag">' + esc(f.short || f.label) + '</span></div>' +
      '<div class="dvh-card__body">' +
        '<p class="dvh-kicker">' + esc(it.kicker || TYPE_LABEL[it.type]) + '</p>' +
        '<h3 class="dvh-card__title">' + esc(it.name) + '</h3>' +
        '<p class="dvh-card__text">' + esc(it.text) + '</p>' +
        '<p class="dvh-card__meta">' + (n ? n + ' related' : 'Profile') + (blog ? ' · <span class="dvh-pill">Blog</span>' : '') + '</p>' +
        '<span class="dvh-card__cta">Explore <span aria-hidden="true">→</span></span>' +
      '</div>' + creditHTML(slotIds(sl)) + '</article>';
  }

  function itemCard(it, d, f, sec) {
    var s = source(it.src), sl = slotFor(sec || it.type, it, d);
    var hasPanel = true;
    return '<article class="dvh-card dvh-card--' + esc(it.type) + ' dvh-reveal">' +
      '<div class="dvh-card__media">' + picHTML(slotIds(sl), fbMeta(it.name, TYPE_LABEL[it.type], it.src), { alt: sl.p && imgRec(sl.id) ? imgRec(sl.id).image_alt : it.name, slot: sl }) + (it.year ? '<span class="dvh-tag dvh-tag--year">' + esc(it.year) + '</span>' : '') + '</div>' +
      '<div class="dvh-card__body">' +
        '<p class="dvh-kicker">' + esc(it.kicker || TYPE_LABEL[it.type]) + '</p>' +
        '<h3 class="dvh-card__title">' + esc(it.name) + '</h3>' +
        '<p class="dvh-card__text">' + esc(it.text) + '</p>' +
        '<p class="dvh-src">Source: ' + (s.url ? '<a href="' + esc(s.url) + '" target="_blank" rel="noopener">' + esc(s.name) + '</a>' : 'DIREVULF') + (it.year ? ' · ' + esc(it.year) : '') + '</p>' +
        '<div class="dvh-card__actions">' +
          (hasPanel ? '<button type="button" class="dvh-btn dvh-btn--sm" data-dvh-open="item" data-id="' + esc(it.id) + '">Explore →</button>' : '') +
          (s.url ? ext(s.url, 'Read original ↗', 'dvh-link dvh-link--sm') : '') +
        '</div>' +
      '</div>' + creditHTML(slotIds(sl), sl.p) + '</article>';
  }

  function factCard(it) {
    var s = source(it.src);
    return '<article class="dvh-fact dvh-reveal"><p class="dvh-fact__label">Did you know?</p><p class="dvh-fact__text">' + esc(it.text) + '</p>' +
      '<p class="dvh-src">Source: ' + (s.url ? '<a href="' + esc(s.url) + '" target="_blank" rel="noopener">' + esc(s.name) + (s.title ? ' — ' + esc(s.title) : '') + '</a>' : 'DIREVULF') + '</p>' +
      (s.url ? ext(s.url, 'Explore →', 'dvh-link dvh-link--sm') : '') + '</article>';
  }

  function blogCard(b, d, f, compact) {
    var it = d.byId[b.char] || {}, sl = slotFor('blogs', b, d, 'blog');
    var read = Math.max(2, Math.round(blogWords(b) / 200));
    return '<article class="dvh-card dvh-card--blog dvh-reveal' + (compact ? ' dvh-card--compact' : '') + '" data-dvh-open="blog" data-id="' + esc(b.id) + '" tabindex="0" role="button" aria-label="Read: ' + esc(b.title) + '">' +
      '<div class="dvh-card__media">' + picHTML(slotIds(sl), fbMeta(b.name || it.name || b.title, (b.cats || [])[0] || 'Story', it.src), { alt: '', sizes: '(max-width: 640px) 84vw, 420px', slot: sl }) + '<span class="dvh-tag">' + esc((b.cats || [])[0] || 'Story') + '</span></div>' +
      '<div class="dvh-card__body">' +
        '<p class="dvh-kicker">' + esc(b.name || it.name || '') + ' · ' + esc(f.short || f.label) + '</p>' +
        '<h3 class="dvh-card__title">' + esc(b.title) + '</h3>' +
        (compact ? '' : '<p class="dvh-card__text">' + esc(b.excerpt) + '</p>') +
        '<p class="dvh-card__meta">' + read + ' min read · Updated ' + esc(fmtDate(new Date((CORE.updated || '') + 'T00:00:00Z'))) + '</p>' +
        '<span class="dvh-card__cta">Read more <span aria-hidden="true">→</span></span>' +
      '</div>' + creditHTML(slotIds(sl)) + '</article>';
  }
  function blogWords(b) {
    var n = words(b.title) + words(b.excerpt);
    ['intro', 'who', 'origin', 'evolution', 'personality', 'abilities'].forEach(function (k) { (b[k] || []).forEach(function (p) { n += words(p.t); }); });
    (b.facts || []).forEach(function (p) { n += words(p.t); });
    (b.timeline || []).forEach(function (p) { n += words(p.t); });
    (b.moments || []).forEach(function (p) { n += words(p.t) + words(p.title); });
    return n;
  }

  function productCard(p) {
    return '<a class="dvh-prod dvh-reveal" href="' + esc(p.u) + '">' +
      '<span class="dvh-prod__media">' + (p.img ? '<img src="' + esc(p.img) + '" srcset="' + esc(p.img) + ' 480w, ' + esc(p.img2 || p.img) + ' 900w" sizes="(max-width:640px) 60vw, 240px" alt="' + esc(p.alt) + '" loading="lazy" decoding="async">' : tile(p.t, 'DIREVULF')) + '</span>' +
      '<span class="dvh-prod__title">' + esc(p.t) + '</span>' +
      '<span class="dvh-prod__price">' + (p.c ? '<s>' + esc(p.c) + '</s> ' : '') + esc(p.p) + (p.a ? '' : ' · Sold out') + '</span></a>';
  }

  function timelineHTML(list) {
    return '<ol class="dvh-timeline dvh-rail" role="list">' + list.map(function (t) {
      var s = source(t.src || t.s);
      return '<li class="dvh-timeline__item dvh-reveal"><span class="dvh-timeline__year">' + esc(t.year || t.y) + '</span><span class="dvh-timeline__dot" aria-hidden="true"></span>' +
        '<p class="dvh-timeline__title">' + esc(t.name || t.t) + '</p>' + (t.text ? '<p class="dvh-timeline__text">' + esc(t.text) + '</p>' : '') +
        (s.url ? '<a class="dvh-src dvh-src--link" href="' + esc(s.url) + '" target="_blank" rel="noopener">' + esc(s.name) + ' ↗</a>' : '') + '</li>';
    }).join('') + '</ol>';
  }

  function sectionHTML(sec, inner, opts) {
    opts = opts || {};
    return '<section class="dvh-sec dvh-sec--' + esc(sec.type) + (opts.cls ? ' ' + opts.cls : '') + '" id="dvh-sec-' + esc(sec.type) + '" aria-labelledby="dvh-h-' + esc(sec.type) + '">' +
      '<div class="dvh-sec__head"><div><p class="dvh-eyebrow">' + esc(opts.eyebrow || sec.eyebrow || '') + '</p><h2 class="dvh-h2" id="dvh-h-' + esc(sec.type) + '">' + esc(opts.title || sec.title) + '</h2></div>' + (opts.right || '') + '</div>' +
      (opts.before || '') + '<div class="dvh-sec__body" data-dvh-slot="' + esc(sec.type) + '">' + inner + '</div></section>';
  }
  function skeletonRow(n) { var s = ''; for (var i = 0; i < (n || 4); i++) s += '<span class="dvh-skel dvh-skel--card"></span>'; return '<div class="dvh-skel-row" aria-hidden="true">' + s + '</div>'; }
  function notice(text, link) { return '<div class="dvh-notice"><p>' + text + '</p>' + (link || '') + '</div>'; }

  /* ------------------------------------------------------------------ fandom rendering */
  function itemsOf(d, type) {
    return (d.items || []).filter(function (i) {
      if (i.type !== type) return false;
      if (STATE.franchise !== 'all' && i.franchise && i.franchise !== STATE.franchise) return false;
      return true;
    });
  }

  function renderSelector() {
    var sel = $('[data-dvh-selector]');
    sel.innerHTML = (CORE.fandoms || []).map(function (f) {
      return '<button type="button" role="tab" class="dvh-selector__btn" data-dvh-fandom="' + esc(f.key) + '" aria-selected="false" style="--dvh-acc:' + esc(f.accent) + '">' +
        '<span class="dvh-selector__full">' + esc(f.label) + '</span><span class="dvh-selector__short">' + esc(f.short || f.label) + '</span></button>';
    }).join('');
  }

  var heroTimer = null;
  function renderHero(f, d) {
    root.style.setProperty('--dvh-acc', f.accent);
    root.style.setProperty('--dvh-acc2', f.accent2 || f.accent);
    $$('[data-dvh-fandom]').forEach(function (b) { var on = b.getAttribute('data-dvh-fandom') === f.key; b.setAttribute('aria-selected', on ? 'true' : 'false'); b.classList.toggle('is-active', on); b.tabIndex = on ? 0 : -1; });
    $('[data-dvh-lede]').textContent = f.intro || '';
    $('[data-dvh-tagline]').textContent = f.tagline || '';
    var hero = $('[data-dvh-hero]'); hero.setAttribute('data-fandom', f.key); root.setAttribute('data-fandom', f.key);
  }
  /* Hero collage: licence-checked photos of this fandom that no card on the page is using, plus
     DIREVULF's own product photos. Runs after the page plan so it never repeats a card image. */
  function renderCollage(f) {
    var pics = [], I = CORE.images || {};
    var ids = Object.keys(I).filter(function (id) { var im = I[id]; return im.fd === f.key && (im.t || 'photo') === 'photo' && (im.w || 0) >= 900; });
    ids.sort(function (a, b) { return ((I[b].r || 1) >= 1.2) - ((I[a].r || 1) >= 1.2); });
    ids.forEach(function (id) {
      if (pics.length >= 4 || !PAGE.free(id) || recBad(id)) return;
      PAGE.take(id, f.key + '-hero', 'hero');
      var r = imgRec(id);
      pics.push({ id: id, src: r.image_url, credit: r.attribution + ' · ' + r.license + ' · Wikimedia Commons', page: r.source_url });
    });
    var col = (CFG.collections || {})[f.collection];
    if (col && col.products) col.products.slice(0, 2).forEach(function (p) { if (p.img2) pics.push({ src: p.img2, credit: 'DIREVULF — ' + p.t, page: p.u }); });
    var c = $('[data-dvh-collage]');
    clearInterval(heroTimer);
    c.innerHTML = pics.map(function (p, i) { return '<img src="' + esc(p.src) + '" alt="" class="' + (i === 0 ? 'is-on' : '') + '" decoding="async" ' + (i ? 'loading="lazy"' : '') + ' data-credit="' + esc(p.credit) + '" data-page="' + esc(p.page) + '"' + (p.id ? ' data-cid="' + esc(f.key) + '-hero" data-sec="hero" data-cur="' + esc(p.id) + '"' : '') + '>'; }).join('') +
      '<span class="dvh-hero__word" aria-hidden="true">' + esc(f.short || f.label) + '</span>';
    var cr = $('[data-dvh-herocredit]');
    function showCredit() { var on = c.querySelector('img.is-on'); if (cr) cr.innerHTML = on ? 'Image: <a href="' + esc(on.getAttribute('data-page')) + '" target="_blank" rel="noopener">' + esc(on.getAttribute('data-credit')) + '</a>' : ''; }
    showCredit();
    if (pics.length > 1 && !reduceMotion()) {
      heroTimer = setInterval(function () {
        var imgs = $$('img', c); if (imgs.length < 2) return;
        var i = imgs.findIndex(function (x) { return x.classList.contains('is-on'); });
        imgs[i].classList.remove('is-on'); imgs[(i + 1) % imgs.length].classList.add('is-on'); showCredit();
      }, 6500);
    }
  }
  function reduceMotion() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

  function renderSubnav(f, d) {
    var secs = visibleSections(f, d);
    $('[data-dvh-subnav-list]').innerHTML = secs.map(function (s) { return '<a class="dvh-chip" href="#dvh-sec-' + esc(s.type) + '">' + esc(s.title) + '</a>'; }).join('');
  }
  function visibleSections(f, d) {
    return (f.sections || []).filter(function (s) {
      if (s.type === 'blogs') return (d.blogs || []).length > 0;
      if (s.type === 'shop' || s.type === 'featured' || s.type.indexOf('f1-') === 0) return true;
      if (s.type === 'products') { var c = (CFG.collections || {})[f.collection]; return !!(c && c.count); }
      if (s.type === 'characters') return itemsOf(d, 'character').length > 0;
      return (d.items || []).some(function (i) { return i.type === s.type; });
    });
  }

  function renderBody(f, d) {
    var html = '', fr = '';
    planPage(f, d);
    if (f.franchises && f.franchises.length) {
      fr = '<div class="dvh-wrap"><div class="dvh-franchises" role="group" aria-label="Filter the stories by franchise">' +
        '<button type="button" class="dvh-chip' + (STATE.franchise === 'all' ? ' is-active' : '') + '" data-dvh-franchise="all" aria-pressed="' + (STATE.franchise === 'all') + '">All anime</button>' +
        f.franchises.map(function (x) { return '<button type="button" class="dvh-chip' + (STATE.franchise === x.key ? ' is-active' : '') + '" data-dvh-franchise="' + esc(x.key) + '" aria-pressed="' + (STATE.franchise === x.key) + '">' + esc(x.name) + '</button>'; }).join('') +
        '</div></div>';
    }
    // Products come first; the franchise filter (anime) sits between the shop and the stories it filters.
    visibleSections(f, d).forEach(function (sec) {
      var fn = RENDER[sec.type] || RENDER._items;
      var out = fn(sec, f, d);
      if (out) html += '<div class="dvh-wrap">' + out + '</div>';
      if (sec.type === 'shop' && fr) { html += fr; fr = ''; }
    });
    if (fr) html = fr + html;
    var body = $('[data-dvh-body]');
    body.innerHTML = html;
    body.setAttribute('data-fandom', f.key);
    renderCollage(f);
    reveal();
    // live modules start after first paint
    LIVE_START.forEach(function (fn) { try { fn(f, d); } catch (e) {} });
  }

  function blogsShown(d) {
    var blogs = (d.blogs || []).filter(function (b) { return STATE.franchise === 'all' || !b.franchise || b.franchise === STATE.franchise; });
    var cats = []; blogs.forEach(function (b) { (b.cats || []).forEach(function (c) { if (cats.indexOf(c) < 0) cats.push(c); }); });
    var active = cats.indexOf(STATE.blogCat) > -1 ? STATE.blogCat : 'all';
    return { blogs: blogs, cats: cats, active: active, shown: blogs.filter(function (b) { return active === 'all' || (b.cats || []).indexOf(active) > -1; }) };
  }
  var RENDER = {
    characters: function (sec, f, d) {
      var list = itemsOf(d, 'character').concat(itemsOf(d, 'driver'));
      if (!list.length) return '';
      return sectionHTML(sec, '<div class="dvh-rail dvh-rail--chars" role="list">' + list.map(function (i) { return charCard(i, d, f); }).join('') + '</div>');
    },
    blogs: function (sec, f, d) {
      var B = blogsShown(d), cats = B.cats, active = B.active, shown = B.shown;
      if (!B.blogs.length) return '';
      var filters = '<div class="dvh-filters" role="group" aria-label="Blog categories"><button type="button" class="dvh-chip' + (active === 'all' ? ' is-active' : '') + '" data-dvh-blogcat="all" aria-pressed="' + (active === 'all') + '">All</button>' +
        cats.map(function (c) { return '<button type="button" class="dvh-chip' + (active === c ? ' is-active' : '') + '" data-dvh-blogcat="' + esc(c) + '" aria-pressed="' + (active === c) + '">' + esc(c) + '</button>'; }).join('') + '</div>';
      return sectionHTML(sec, '<div class="dvh-grid dvh-grid--blogs">' + shown.map(function (b) { return blogCard(b, d, f); }).join('') + '</div>', { before: filters });
    },
    fact: function (sec, f, d) {
      var list = itemsOf(d, 'fact'); if (!list.length) return '';
      return sectionHTML(sec, '<div class="dvh-rail dvh-rail--facts">' + list.map(factCard).join('') + '</div>');
    },
    timeline: function (sec, f, d) {
      var list = itemsOf(d, 'timeline').slice().sort(function (a, b) { return a.year - b.year; }); if (!list.length) return '';
      return sectionHTML(sec, timelineHTML(list));
    },
    products: function (sec, f) {
      // "More [fandom] products": department shortcuts at the end of the page (no repeated cards).
      var col = (CFG.collections || {})[f.collection];
      if (!col || !col.count) return '';
      var links = '<a class="dvh-morebar__link" href="' + esc(col.url) + '"><span>All ' + esc(f.name) + '</span><b>' + col.count + '</b></a>' +
        (col.depts || []).filter(function (x) { return x.count > 0; }).map(function (x) { return '<a class="dvh-morebar__link" href="' + esc(x.url) + '"><span>' + esc(f.name) + ' · ' + esc(x.label) + '</span><b>' + x.count + '</b></a>'; }).join('');
      return sectionHTML(sec, '<nav class="dvh-morebar" aria-label="' + esc(sec.title) + '">' + links + '</nav>', { eyebrow: sec.eyebrow });
    },
    _items: function (sec, f, d) {
      var list = itemsOf(d, sec.type); if (!list.length) return '';
      var cls = sec.type === 'story' || sec.type === 'event' || sec.type === 'moment' ? 'dvh-rail' : 'dvh-grid';
      return sectionHTML(sec, '<div class="' + cls + ' dvh-list--' + esc(sec.type) + '">' + list.map(function (i) { return itemCard(i, d, f, sec.type); }).join('') + '</div>');
    }
  };
  var LIVE_START = [];

  /* ------------------------------------------------------------------ SHOP [FANDOM] (first section)
     Products come from the fandom's Shopify collection — the smart collection built from each
     product's direvulf.fandom metafield — so Men, Women and Accessories are all included and
     nothing is matched by keywords. The first 12 cards are rendered by Liquid with the theme's own
     product-card snippet (<template data-dvh-shop>); Load more and the department chips request
     further pages of the same collection (or its department child collection) through the Section
     Rendering API: /collections/HANDLE?section_id=fandom-hub-cards&page=N. */
  var SHOP = { f: null, handle: '', dept: '', page: 1, pages: 1, count: 0, busy: false };
  RENDER.shop = function (sec, f) {
    var col = (CFG.collections || {})[f.collection];
    var n = col ? col.count : 0;
    var all = '<a class="dvh-btn dvh-btn--ghost" href="' + esc(CFG.allUrl || '/collections/all') + '">Shop all DIREVULF →</a>';
    if (!col || !n) {
      return sectionHTML(sec, notice('No DIREVULF ' + esc(f.name) + ' pieces are live yet — new ' + esc(f.name) + ' drops will appear here first.', all), { eyebrow: f.name, title: sec.title });
    }
    var depts = (col.depts || []).filter(function (x) { return x.count > 0; });
    var chips = depts.length ? '<div class="dvh-filters dvh-shop__depts" role="group" aria-label="Filter ' + esc(f.name) + ' products by department">' +
      '<button type="button" class="dvh-chip is-active" data-dvh-dept="" aria-pressed="true">All <span class="dvh-chip__n">' + n + '</span></button>' +
      depts.map(function (x) { return '<button type="button" class="dvh-chip" data-dvh-dept="' + esc(x.handle) + '" aria-pressed="false">' + esc(x.label) + ' <span class="dvh-chip__n">' + x.count + '</span></button>'; }).join('') + '</div>' : '';
    var right = '<a class="dvh-btn dvh-btn--ghost dvh-shop__all" href="' + esc(col.url) + '">Shop all ' + esc(f.name) + ' →</a>';
    return sectionHTML(sec,
      '<p class="dvh-shop__count" data-dvh-shop-count aria-live="polite">' + n + (n === 1 ? ' style' : ' styles') + '</p>' +
      '<div class="product-grid dvh-shop__grid" data-dvh-shop-grid></div>' +
      '<div class="dvh-shop__foot" data-dvh-shop-foot></div>', { eyebrow: f.name, title: sec.title, right: right, before: chips, cls: 'dvh-shop' });
  };
  function shopTemplate(handle) {
    var t = null; $$('template[data-dvh-shop]').forEach(function (x) { if (x.getAttribute('data-dvh-shop') === handle) t = x; });
    return t;
  }
  function wishRepaint() { try { window.dispatchEvent(new StorageEvent('storage', { key: 'dv:wishlist' })); } catch (e) {} }
  function shopFoot() {
    var foot = $('[data-dvh-shop-foot]'); if (!foot) return;
    var shown = $$('[data-dvh-shop-grid] .product-card').length;
    foot.innerHTML = SHOP.page < SHOP.pages ? '<button type="button" class="dvh-btn dvh-btn--ghost" data-dvh-shop-more>Load more <span class="dvh-chip__n">' + shown + ' / ' + SHOP.count + '</span></button>' : (SHOP.count > 12 ? '<p class="dvh-meta-line">All ' + SHOP.count + ' shown.</p>' : '');
    var cnt = $('[data-dvh-shop-count]'); if (cnt) cnt.textContent = SHOP.count + (SHOP.count === 1 ? ' style' : ' styles');
  }
  function shopFetch(handle, page) {
    var base = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
    var url = base + '/collections/' + encodeURIComponent(handle) + '?section_id=fandom-hub-cards&page=' + page;
    var ctrl = window.AbortController ? new AbortController() : null, timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 12000);
    return fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var box = document.createElement('div'); box.innerHTML = html;
        var meta = box.querySelector('[data-dvh-cards]'); if (!meta) throw new Error('no cards');
        return { cards: $$('.product-card', meta), count: +meta.getAttribute('data-count') || 0, pages: +meta.getAttribute('data-pages') || 1 };
      })
      .finally(function () { clearTimeout(timer); });
  }
  function shopError(handle) {
    var foot = $('[data-dvh-shop-foot]'); if (!foot) return;
    foot.innerHTML = notice('More products couldn’t load right now.', '<a class="dvh-btn dvh-btn--ghost" href="' + esc('/collections/' + handle) + '">Open the collection →</a>');
  }
  function shopInit(f) {
    var grid = $('[data-dvh-shop-grid]'), col = (CFG.collections || {})[f.collection];
    SHOP = { f: f, handle: f.collection, dept: '', page: 1, pages: 1, count: col ? col.count : 0, busy: false };
    if (!grid || !col) return;
    SHOP.pages = Math.max(1, Math.ceil(SHOP.count / 12));
    var t = shopTemplate(f.collection);
    if (t) { grid.appendChild(t.content.cloneNode(true)); wishRepaint(); shopFoot(); return; }
    shopLoad(f.collection, 1, true);
  }
  function shopLoad(handle, page, replace) {
    var grid = $('[data-dvh-shop-grid]'); if (!grid || SHOP.busy) return;
    SHOP.busy = true; grid.setAttribute('aria-busy', 'true');
    if (replace) grid.innerHTML = skeletonCards(4);
    var btn = $('[data-dvh-shop-more]'); if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    var fk = SHOP.f.key;
    shopFetch(handle, page).then(function (r) {
      if (!SHOP.f || SHOP.f.key !== fk || SHOP.handle !== handle) return;
      if (replace) grid.innerHTML = '';
      r.cards.forEach(function (c) { grid.appendChild(c); });
      SHOP.page = page; SHOP.pages = r.pages; SHOP.count = r.count;
      wishRepaint(); shopFoot();
    }).catch(function () { if (replace) grid.innerHTML = ''; shopError(handle); })
      .finally(function () { SHOP.busy = false; grid.removeAttribute('aria-busy'); });
  }
  function shopMore() { if (SHOP.page < SHOP.pages) shopLoad(SHOP.handle, SHOP.page + 1, false); }
  function shopDept(handle) {
    var f = SHOP.f; if (!f) return;
    $$('[data-dvh-dept]').forEach(function (b) { var on = b.getAttribute('data-dvh-dept') === handle; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    SHOP.busy = false;
    if (!handle) { var grid = $('[data-dvh-shop-grid]'); grid.innerHTML = ''; shopInit(f); return; }
    var col = (CFG.collections || {})[f.collection] || {}, dep = (col.depts || []).filter(function (x) { return x.handle === handle; })[0];
    SHOP.handle = handle; SHOP.dept = handle; SHOP.page = 1; SHOP.count = dep ? dep.count : 0; SHOP.pages = Math.max(1, Math.ceil(SHOP.count / 12));
    shopLoad(handle, 1, true);
  }
  function skeletonCards(n) { var h = ''; for (var i = 0; i < n; i++) h += '<span class="dvh-skel dvh-skel--prod" aria-hidden="true"></span>'; return h; }
  LIVE_START.push(function (f) { shopInit(f); });

  /* scroll reveal */
  var io = null;
  function reveal() {
    var els = $$('.dvh-reveal:not(.is-in)');
    if (reduceMotion() || !('IntersectionObserver' in window)) { els.forEach(function (e) { e.classList.add('is-in'); }); return; }
    if (!io) io = new IntersectionObserver(function (en) { en.forEach(function (x) { if (x.isIntersecting) { x.target.classList.add('is-in'); io.unobserve(x.target); } }); }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ------------------------------------------------------------------ live sources */
  // Wikipedia summary (CC BY-SA 4.0). Cached 7 days.
  function wikiSummary(title) {
    if (!LIVE.wikipedia) return Promise.reject(new Error('off'));
    return cached('wp:' + title, 7 * DAY, function () {
      return queued(function () {
        return getJSON('https://en.wikipedia.org/api/rest_v1/page/summary/' + encodeURIComponent(title), { headers: UA }).then(function (j) {
          if (!j || !j.extract || j.type === 'disambiguation') throw new Error('no extract');
          return { title: j.title, extract: j.extract, url: j.content_urls && j.content_urls.desktop && j.content_urls.desktop.page, wd: j.wikibase_item, ts: j.timestamp };
        });
      });
    });
  }
  // Fandom wiki lead section (CC BY-SA 3.0). Cached 7 days.
  function fandomLead(wiki, title) {
    if (!LIVE.fandom) return Promise.reject(new Error('off'));
    return cached('fd:' + wiki + ':' + title, 7 * DAY, function () {
      return queued(function () {
        return getJSON('https://' + wiki + '.fandom.com/api.php?action=parse&redirects=1&prop=text&section=0&format=json&origin=*&disablelimitreport=1&page=' + encodeURIComponent(title)).then(function (j) {
          if (!j.parse) throw new Error('missing');
          var doc = new DOMParser().parseFromString(j.parse.text['*'], 'text/html');
          $$('aside, table, sup, figure, style, script, .reference', doc).forEach(function (e) { e.remove(); });
          var t = $$('p', doc).map(function (p) { return p.textContent.trim(); }).filter(Boolean).join(' ').replace(/\s+/g, ' ');
          if (!t) throw new Error('empty');
          return { title: j.parse.title, extract: t.slice(0, 900), url: 'https://' + wiki + '.fandom.com/wiki/' + encodeURIComponent(j.parse.title.replace(/ /g, '_')) };
        });
      });
    });
  }
  function overview(code) {
    var s = source(code);
    if (s.api === 'wp') return wikiSummary(s.key);
    if (s.api === 'fd') return fandomLead(s.wiki, s.key);
    return Promise.reject(new Error('no live source'));
  }
  // Wikidata facts (CC0): creators, first appearance, member of, abilities. Cached 14 days.
  var WD_PROPS = { P170: 'Created by', P4584: 'First appearance', P463: 'Member of', P2563: 'Abilities' };
  function wikidataFacts(qid) {
    if (!LIVE.wikipedia || !qid) return Promise.reject(new Error('off'));
    return cached('wd:' + qid, 14 * DAY, function () {
      return queued(function () {
        return getJSON('https://www.wikidata.org/w/api.php?action=wbgetentities&props=claims&format=json&origin=*&ids=' + qid, { headers: UA });
      }).then(function (j) {
        var e = j.entities && j.entities[qid], out = {}, need = [];
        Object.keys(WD_PROPS).forEach(function (p) {
          var ids = ((e && e.claims && e.claims[p]) || []).map(function (c) { return c.mainsnak && c.mainsnak.datavalue && c.mainsnak.datavalue.value && c.mainsnak.datavalue.value.id; }).filter(Boolean).slice(0, 6);
          if (ids.length) { out[p] = ids; need = need.concat(ids); }
        });
        if (!need.length) return {};
        return queued(function () {
          return getJSON('https://www.wikidata.org/w/api.php?action=wbgetentities&props=labels&languages=en&format=json&origin=*&ids=' + need.slice(0, 50).join('|'), { headers: UA });
        }).then(function (l) {
          var res = {};
          Object.keys(out).forEach(function (p) {
            res[WD_PROPS[p]] = out[p].map(function (id) { var x = l.entities[id]; return x && x.labels && x.labels.en ? x.labels.en.value : null; }).filter(Boolean);
          });
          return res;
        });
      });
    });
  }

  // ComicNewbies — headline, date and link only (no text or images reproduced). Cached 6 hours.
  var CN_EXCLUDE = '3263,7937781,24109057,9563102,170963126,183685569,598383100,172719987,19262405,10861670,163737176,163740709,192928971,698126333,651413854,698126125,48539758,308178302,162317681';
  function comicNewbies(cat) {
    if (!LIVE.comicnewbies || !cat) return Promise.reject(new Error('off'));
    return cached('cn:' + cat, 6 * HOUR, function () {
      return getJSON('https://comicnewbies.com/wp-json/wp/v2/posts?per_page=8&categories=' + cat + '&categories_exclude=' + CN_EXCLUDE + '&_fields=id,date_gmt,modified_gmt,link,title,categories').then(function (list) {
        if (!Array.isArray(list)) throw new Error('bad');
        return list.map(function (p) { return { id: p.id, date: p.date_gmt + 'Z', modified: p.modified_gmt + 'Z', url: p.link, title: decodeEntities(p.title && p.title.rendered), cats: p.categories }; })
          .filter(function (p) { return p.title && /^https:\/\/comicnewbies\.com\//.test(p.url); });
      });
    });
  }
  function cnCharIndex(d) { var m = {}; (d.items || []).forEach(function (i) { if (i.cn) m[i.cn] = i; }); return m; }
  function cnCard(p, d, f) {
    var idx = cnCharIndex(d), chars = (p.cats || []).map(function (c) { return idx[c]; }).filter(Boolean), cands = [];
    chars.forEach(function (c) { ownImgs(c).forEach(function (x) { if (imgRec(x)) cands.push({ id: x, t: 3, p: c.name }); }); });
    var sl = pickSlot(PAGE, cands, (STATE.fandom || '') + '-cn-' + p.id, 'featured');
    var lead = (sl.p && chars.filter(function (c) { return c.name === sl.p; })[0]) || chars[0];
    var dt = new Date(p.date), isNew = Date.now() - dt < (CFG.latestDays || 30) * DAY;
    return '<article class="dvh-card dvh-card--news dvh-reveal">' +
      '<div class="dvh-card__media">' + picHTML(slotIds(sl), fbMeta(lead ? lead.name : p.title, 'Comic story', ''), { alt: sl.id ? imgRec(sl.id).image_alt : '', slot: sl }) + (isNew ? '<span class="dvh-tag dvh-tag--new">New</span>' : '') + '</div>' +
      '<div class="dvh-card__body"><p class="dvh-kicker">' + esc(chars.length ? chars.map(function (c) { return c.name; }).slice(0, 3).join(' · ') : f.name) + '</p>' +
      '<h3 class="dvh-card__title">' + esc(p.title) + '</h3>' +
      '<p class="dvh-src">Source: ComicNewbies · <time datetime="' + esc(p.date) + '">' + esc(fmtDate(dt)) + '</time></p>' +
      '<div class="dvh-card__actions">' + ext(p.url, 'Read on ComicNewbies ↗', 'dvh-btn dvh-btn--sm') + (lead ? '<button type="button" class="dvh-link dvh-link--sm" data-dvh-open="item" data-id="' + esc(lead.id) + '">' + esc(lead.name) + ' →</button>' : '') + '</div>' +
      '</div>' + creditHTML(slotIds(sl), sl.p) + '</article>';
  }
  RENDER.featured = function (sec, f, d) {
    if (!f.cn_category) return '';
    return sectionHTML(sec, skeletonRow(4), { title: 'Comic stories', eyebrow: 'From ComicNewbies' });
  };
  LIVE_START.push(function (f, d) {
    var slot = $('[data-dvh-slot="featured"]'); if (!slot) return;
    var sec = slot.closest('.dvh-sec');
    function fallback(msg) {
      // No live headlines and no saved copy: drop this block (the curated Stories section follows) and say so there.
      sec.remove();
      var chip = $('[data-dvh-subnav-list] a[href="#dvh-sec-featured"]'); if (chip) chip.remove();
      var st = $('[data-dvh-slot="story"]');
      if (st && !st.querySelector('.dvh-notice')) st.insertAdjacentHTML('afterbegin', notice(msg));
    }
    comicNewbies(f.cn_category).then(function (r) {
      if (STATE.fandom !== f.key) return;
      var posts = (r.v || []).slice().sort(function (a, b) { return new Date(b.date) - new Date(a.date); });
      if (!posts.length) return fallback('No new comic headlines right now — here are DIREVULF’s featured stories.');
      var newest = new Date(posts[0].date), latest = Date.now() - newest < (CFG.latestDays || 30) * DAY;
      sec.querySelector('.dvh-h2').textContent = latest ? 'Latest comic stories' : 'Recent comic stories';
      sec.querySelector('.dvh-eyebrow').textContent = 'Headlines from ComicNewbies';
      slot.innerHTML = '<div class="dvh-rail">' + posts.map(function (p) { return cnCard(p, d, f); }).join('') + '</div>' +
        '<p class="dvh-meta-line">' + (r.stale ? 'Showing saved headlines · ' : '') + 'Checked ' + esc(fmtDate(new Date(r.t))) + ' · Headlines link to ComicNewbies; their text and images are not reproduced here. · ' + ext('https://comicnewbies.com/', 'comicnewbies.com ↗', 'dvh-link dvh-link--sm') + '</p>';
      reveal();
    }).catch(function () { if (STATE.fandom === f.key) fallback('Live comic headlines are unavailable right now, so here are DIREVULF’s featured stories.'); });
  });

  /* ------------------------------------------------------------------ F1 (Wikipedia season article, CC BY-SA 4.0) */
  function seasonTitle(f, year) { return (f.season_page || '{year}_Formula_One_World_Championship').replace('{year}', year); }
  function f1Season(f) {
    if (!LIVE.f1) return Promise.reject(new Error('off'));
    var year = new Date().getUTCFullYear();
    return cached('f1season:' + year, 3 * HOUR, function () {
      var title = seasonTitle(f, year);
      var api = 'https://en.wikipedia.org/w/api.php?format=json&origin=*&redirects=1&page=' + encodeURIComponent(title);
      return queued(function () { return getJSON(api + '&action=parse&prop=sections|revid', { headers: UA }); }).then(function (s) {
        if (!s.parse) throw new Error('no season page');
        var secs = s.parse.sections, find = function (re) { var x = secs.filter(function (v) { return re.test(v.line.replace(/<[^>]+>/g, '')); })[0]; return x && x.index; };
        var want = { drivers: find(/Drivers'? Championship standings/i), teams: find(/Constructors'? Championship standings/i), cal: find(/^Calendar$/i), gp: find(/^Grands Prix$/i) };
        var keys = Object.keys(want).filter(function (k) { return want[k]; });
        return Promise.all(keys.map(function (k) { return queued(function () { return getJSON(api + '&action=parse&prop=text&disableeditsection=1&disablelimitreport=1&section=' + want[k], { headers: UA }); }); }))
          .then(function (res) {
            var out = { year: year, title: s.parse.title, revid: s.parse.revid, url: 'https://en.wikipedia.org/wiki/' + encodeURIComponent(s.parse.title.replace(/ /g, '_')) };
            keys.forEach(function (k, i) { out[k] = parseF1Table(k, res[i].parse.text['*'], year); });
            if (!out.drivers || !out.drivers.length) throw new Error('no standings');
            return out;
          });
      });
    });
  }
  function cellText(c) { var x = c.cloneNode(true); $$('sup, .reference, style', x).forEach(function (e) { e.remove(); }); return x.textContent.replace(/\s+/g, ' ').trim(); }
  function wikiHref(c) { var a = c && c.querySelector('a[href^="/wiki/"]'); return a ? 'https://en.wikipedia.org' + a.getAttribute('href') : null; }
  function parseF1Table(kind, html, year) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var t = doc.querySelector('table.wikitable'); if (!t) return [];
    var rows = $$('tr', t), head = rows[0] ? $$('th,td', rows[0]).map(cellText) : [];
    var out = [];
    rows.slice(1).forEach(function (r) {
      var c = $$('th,td', r); if (c.length < 3) return;
      var first = cellText(c[0]);
      if (kind === 'drivers' || kind === 'teams') {
        if (!/^\d+$/.test(first)) return;
        out.push({ pos: +first, name: cellText(c[1]).replace(/^\W+/, ''), url: wikiHref(c[1]), pts: cellText(c[c.length - 1]) });
      } else if (kind === 'cal') {
        if (!/^\d+$/.test(first)) return;
        var dateTxt = cellText(c[3] || c[c.length - 1]), dt = new Date(dateTxt + ' ' + year + ' 12:00 UTC');
        out.push({ round: +first, gp: cellText(c[1]), url: wikiHref(c[1]), circuit: cellText(c[2]), date: isNaN(dt) ? null : dt.toISOString(), dateText: dateTxt });
      } else if (kind === 'gp') {
        if (!/^\d+$/.test(first)) return;
        var wi = head.findIndex(function (h) { return /winning driver/i.test(h); }), ci = head.findIndex(function (h) { return /winning constructor/i.test(h); });
        var gi = head.findIndex(function (h) { return /grand prix/i.test(h); });
        var w = wi > -1 && c[wi] ? cellText(c[wi]) : '';
        if (w) out.push({ round: +first, gp: gi > -1 ? cellText(c[gi]) : '', winner: w, team: ci > -1 && c[ci] ? cellText(c[ci]) : '' });
      }
    });
    return out;
  }
  function f1Attribution(r) {
    return '<p class="dvh-meta-line">' + (r.stale ? 'Showing saved data · ' : 'Live · ') + 'updated ' + esc(new Date(r.t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })) +
      ' · Source: ' + ext(r.v.url, 'Wikipedia — ' + esc(r.v.title), 'dvh-link dvh-link--sm') + ' (CC BY-SA 4.0). DIREVULF is not affiliated with Formula 1.</p>';
  }
  function f1Unavailable() {
    return notice('Live standings are unavailable right now. The full season is on Wikipedia and the official site.',
      ext('https://en.wikipedia.org/wiki/' + seasonTitle({}, new Date().getUTCFullYear()), 'Season on Wikipedia ↗', 'dvh-btn dvh-btn--ghost') + ' ' + ext('https://www.formula1.com/en/latest', 'News on www.formula1.com ↗', 'dvh-btn dvh-btn--ghost'));
  }
  function nextRace(v) {
    var now = Date.now();
    return (v.cal || []).filter(function (x) { return x.date && new Date(x.date).getTime() + 36e5 * 12 >= now; })[0] || null;
  }
  function lastResult(v) { var g = v.gp || []; return g.length ? g[g.length - 1] : null; }

  RENDER['f1-news'] = function (sec, f) {
    var N = CORE.f1_news || {};
    var cats = (N.categories || []).map(function (c) { return ext(c.url, esc(c.label) + ' ↗', 'dvh-chip dvh-chip--ext'); }).join('');
    return sectionHTML(sec,
      '<div class="dvh-wire" data-dvh-wire>' + skeletonRow(2) + '</div>' +
      '<div class="dvh-f1news" data-dvh-f1feed></div>' +
      '<div class="dvh-f1link"><div><p class="dvh-f1link__title">Official Formula 1 news</p><p class="dvh-f1link__text">Every story, analysis and technical piece is published by Formula 1 on www.formula1.com. DIREVULF links to it rather than copying it.</p></div>' +
      ext(N.home || 'https://www.formula1.com/en/latest', 'Read official F1 news on www.formula1.com →', 'dvh-btn') + '</div>' +
      '<div class="dvh-filters dvh-filters--ext" aria-label="Formula 1 news categories on www.formula1.com">' + cats + '</div>');
  };
  LIVE_START.push(function (f) {
    if (f.key !== 'f1') return;
    var wire = $('[data-dvh-wire]'), champ = $('[data-dvh-slot="f1-championship"]'), wk = $('[data-dvh-slot="f1-weekend"]'), dr = $('[data-dvh-slot="f1-drivers"]'), tm = $('[data-dvh-slot="f1-teams"]');
    renderF1Feed();
    f1Season(f).then(function (r) {
      if (STATE.fandom !== 'f1') return;
      var v = r.v, nx = nextRace(v), last = lastResult(v);
      if (wire) wire.innerHTML = '<div class="dvh-wire__grid">' +
        (last ? '<div class="dvh-wire__card"><p class="dvh-kicker">Latest result · Round ' + esc(last.round) + '</p><p class="dvh-wire__big">' + esc(last.gp || '') + '</p><p>Won by <strong>' + esc(last.winner) + '</strong>' + (last.team ? ' · ' + esc(last.team) : '') + '</p></div>' : '') +
        (nx ? '<div class="dvh-wire__card dvh-wire__card--acc"><p class="dvh-kicker">Next race · Round ' + esc(nx.round) + '</p><p class="dvh-wire__big">' + esc(nx.gp) + '</p><p>' + esc(nx.circuit) + ' · ' + esc(fmtDate(new Date(nx.date))) + ' · ' + countdown(nx.date) + '</p></div>' : '') +
        (v.drivers[0] ? '<div class="dvh-wire__card"><p class="dvh-kicker">Championship leader</p><p class="dvh-wire__big">' + esc(v.drivers[0].name) + '</p><p>' + esc(v.drivers[0].pts) + ' points</p></div>' : '') +
        '</div>' + f1Attribution(r);
      if (champ) champ.innerHTML = '<div class="dvh-standings">' +
        standings('Drivers', v.drivers.slice(0, 10)) + standings('Constructors', (v.teams || []).slice(0, 10)) + '</div>' + f1Attribution(r);
      if (wk) wk.innerHTML = nx ? '<div class="dvh-rail dvh-rail--cal">' + (v.cal || []).map(function (c) {
          var res = (v.gp || []).filter(function (g) { return g.round === c.round; })[0], isNext = nx && c.round === nx.round;
          return '<article class="dvh-race' + (res ? ' is-done' : '') + (isNext ? ' is-next' : '') + '"><p class="dvh-kicker">Round ' + esc(c.round) + (isNext ? ' · Next' : '') + '</p><h3 class="dvh-card__title">' + esc(c.gp) + '</h3><p class="dvh-card__text">' + esc(c.circuit) + '</p><p class="dvh-race__date">' + esc(c.date ? fmtDate(new Date(c.date)) : c.dateText) + '</p>' + (res ? '<p class="dvh-race__win">Winner: ' + esc(res.winner) + '</p>' : '') + (c.url ? ext(c.url, 'Race page ↗', 'dvh-link dvh-link--sm') : '') + '</article>';
        }).join('') + '</div>' + f1Attribution(r) : notice('The ' + v.year + ' season calendar is complete.', ext(v.url, 'Season results on Wikipedia ↗', 'dvh-btn dvh-btn--ghost'));
      if (dr) dr.innerHTML = '<div class="dvh-grid dvh-grid--drivers">' + v.drivers.slice(0, 22).map(function (x) {
          return '<article class="dvh-driver"><span class="dvh-driver__pos">P' + esc(x.pos) + '</span><div><h3 class="dvh-card__title">' + esc(x.name) + '</h3><p class="dvh-card__text">' + esc(x.pts) + ' pts</p></div>' + (x.url ? ext(x.url, 'Profile ↗', 'dvh-link dvh-link--sm') : '') + '</article>';
        }).join('') + '</div>' + f1Attribution(r);
      if (tm) tm.innerHTML = '<div class="dvh-grid dvh-grid--teams">' + (v.teams || []).map(function (x) {
          return '<article class="dvh-team"><span class="dvh-driver__pos">P' + esc(x.pos) + '</span><div><h3 class="dvh-card__title">' + esc(x.name) + '</h3><p class="dvh-card__text">' + esc(x.pts) + ' pts</p></div>' + (x.url ? ext(x.url, 'Team history ↗', 'dvh-link dvh-link--sm') : '') + '</article>';
        }).join('') + '</div>' + f1Attribution(r);
    }).catch(function () {
      if (STATE.fandom !== 'f1') return;
      [wire, champ, wk, dr, tm].forEach(function (el) { if (el) el.innerHTML = f1Unavailable(); });
    });
  });
  function standings(label, rows) {
    return '<div class="dvh-table"><p class="dvh-table__title">' + esc(label) + '</p><ol role="list">' + rows.map(function (x) {
      return '<li><span class="dvh-table__pos">' + esc(x.pos) + '</span><span class="dvh-table__name">' + (x.url ? '<a href="' + esc(x.url) + '" target="_blank" rel="noopener">' + esc(x.name) + '</a>' : esc(x.name)) + '</span><span class="dvh-table__pts">' + esc(x.pts) + '</span></li>';
    }).join('') + '</ol></div>';
  }
  function countdown(iso) {
    var days = Math.ceil((new Date(iso) - Date.now()) / DAY);
    return days > 1 ? 'in ' + days + ' days' : days === 1 ? 'tomorrow' : 'race weekend';
  }
  ['f1-championship', 'f1-weekend', 'f1-drivers', 'f1-teams'].forEach(function (t) {
    RENDER[t] = function (sec) { return sectionHTML(sec, skeletonRow(4)); };
  });

  /* Licensed F1 news feed adapter (inactive unless CORE.f1_news.feed is set to a JSON feed the
     store is licensed to display). Items: {id, url, title, image, category, excerpt, published_at, updated_at}.
     Newest first, de-duplicated by id/url, kept as a rolling local archive, paged 8 at a time. */
  function renderF1Feed() {
    var host = $('[data-dvh-f1feed]'), N = CORE.f1_news || {};
    if (!host || !N.feed) return;
    cached('f1news', 30 * 60 * 1000, function () { return getJSON(N.feed); }).then(function (r) {
      var seen = {}, all = [];
      var arch = (store.get('f1news-archive') || { v: [] }).v.concat(r.v || []);
      arch.forEach(function (a) { var k = a.id || a.url; if (!k || !a.title || !a.url) return; if (!seen[k] || new Date(a.updated_at || a.published_at) > new Date(seen[k].updated_at || seen[k].published_at)) seen[k] = a; });
      Object.keys(seen).forEach(function (k) { all.push(seen[k]); });
      all.sort(function (a, b) { return new Date(b.published_at) - new Date(a.published_at); });
      all = all.slice(0, 200); store.set('f1news-archive', all);
      var cats = []; all.forEach(function (a) { if (a.category && cats.indexOf(a.category) < 0) cats.push(a.category); });
      var state = { cat: 'All', n: 8 };
      function draw() {
        var list = all.filter(function (a) { return state.cat === 'All' || a.category === state.cat; });
        host.innerHTML = '<div class="dvh-filters">' + ['All'].concat(cats).map(function (c) { return '<button type="button" class="dvh-chip' + (c === state.cat ? ' is-active' : '') + '" data-f1cat="' + esc(c) + '">' + esc(c) + '</button>'; }).join('') + '</div>' +
          '<div class="dvh-grid dvh-grid--news">' + list.slice(0, state.n).map(function (a) {
            var dt = new Date(a.published_at), isNew = Date.now() - dt < 2 * DAY;
            return '<article class="dvh-card dvh-card--news"><div class="dvh-card__media">' + (a.image ? '<img class="dvh-img" src="' + esc(a.image) + '" alt="" loading="lazy" decoding="async" data-dvh-img data-label="F1" data-kind="News">' : tile('F1', 'News')) + (isNew ? '<span class="dvh-tag dvh-tag--new">New</span>' : '') + '</div>' +
              '<div class="dvh-card__body"><p class="dvh-kicker">' + esc(a.category || 'News') + '</p><h3 class="dvh-card__title">' + esc(a.title) + '</h3>' + (a.excerpt ? '<p class="dvh-card__text">' + esc(a.excerpt) + '</p>' : '') +
              '<p class="dvh-src">' + esc(N.source_name || 'Formula 1') + ' · ' + esc(fmtDate(dt)) + '</p>' + ext(a.url, 'Read article →', 'dvh-btn dvh-btn--sm') + '</div></article>';
          }).join('') + '</div>' + (list.length > state.n ? '<button type="button" class="dvh-btn dvh-btn--ghost" data-f1more>Load more</button>' : '') +
          '<p class="dvh-meta-line">Last updated ' + esc(new Date(r.t).toLocaleString('en-GB')) + '</p>';
      }
      host.addEventListener('click', function (e) {
        var c = e.target.closest('[data-f1cat]'); if (c) { state.cat = c.getAttribute('data-f1cat'); state.n = 8; draw(); }
        if (e.target.closest('[data-f1more]')) { state.n += 8; draw(); }
      });
      draw();
    }).catch(function () {});
  }

  /* ------------------------------------------------------------------ layer (item panel + article) */
  var layer = $('[data-dvh-layer]'), lastFocus = null, prevOverflow = '';
  function openLayer(html, kind, label) {
    lastFocus = document.activeElement;
    layer.innerHTML = '<div class="dvh-layer__scrim" data-dvh-close></div>' +
      '<div class="dvh-layer__dialog dvh-layer__dialog--' + kind + '" role="dialog" aria-modal="true" aria-label="' + esc(label) + '" tabindex="-1">' +
      '<button type="button" class="dvh-layer__close" data-dvh-close aria-label="Close">×</button><div class="dvh-layer__scroll">' + html + '</div></div>';
    layer.hidden = false;
    if (!document.documentElement.classList.contains('dvh-locked')) { prevOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; document.documentElement.classList.add('dvh-locked'); }
    requestAnimationFrame(function () { layer.classList.add('is-open'); var dlg = $('.dvh-layer__dialog', layer); dlg.focus({ preventScroll: true }); $('.dvh-layer__scroll', layer).scrollTop = 0; });
    document.addEventListener('keydown', onKey);
    reveal();
  }
  function closeLayer(fromHistory) {
    if (layer.hidden) return;
    layer.classList.remove('is-open');
    layer.hidden = true; layer.innerHTML = '';
    document.documentElement.style.overflow = prevOverflow; document.documentElement.classList.remove('dvh-locked');
    document.removeEventListener('keydown', onKey);
    if (!fromHistory) { if (history.state && history.state.dvhv) history.back(); else setUrl({ view: null }, true); }
    if (lastFocus && lastFocus.focus) try { lastFocus.focus({ preventScroll: true }); } catch (e) {}
  }
  function onKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeLayer(); }
    if (e.key === 'Tab') {
      var f = $$('a[href], button:not([disabled]), [tabindex="0"]', layer).filter(function (x) { return x.offsetParent !== null; });
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  }

  function productsFor(f, names) {
    var col = (CFG.collections || {})[f.collection];
    if (!col || !col.products || !col.products.length) return { col: col, list: [] };
    var toks = [];
    (names || []).forEach(function (n) { norm(n).split(' ').forEach(function (t) { if (t.length > 3 && ['the', 'with', 'from'].indexOf(t) < 0) toks.push(t); }); });
    var matched = col.products.filter(function (p) { var h = norm(p.t + ' ' + p.tags); return toks.some(function (t) { return h.indexOf(t) > -1; }); });
    return { col: col, list: (matched.length ? matched : col.products).slice(0, 4), matched: matched.length > 0 };
  }
  function shopBlock(f, names, title) {
    var r = productsFor(f, names);
    if (!r.col) return '';
    if (!r.list.length) return '<section class="dvh-block dvh-block--shop"><h3 class="dvh-h3">' + esc(title) + '</h3>' + notice('The DIREVULF ' + esc(f.name) + ' drop isn’t live yet.', '<a class="dvh-btn dvh-btn--ghost" href="' + esc(CFG.allUrl || '/collections/all') + '">Shop all DIREVULF →</a>') + '</section>';
    return '<section class="dvh-block dvh-block--shop"><h3 class="dvh-h3">' + esc(title) + '</h3><div class="dvh-rail dvh-rail--prods">' + r.list.map(productCard).join('') + '</div><a class="dvh-btn dvh-btn--ghost" href="' + esc(r.col.url) + '">Shop the fandom →</a></section>';
  }
  function miniCard(it, d, A, sec) {
    var isBlog = it.title && it.char, name = isBlog ? it.title : it.name;
    var sl = pickSlot(A || LAYER, isBlog ? blogCands(it, d) : itemCands(it, d), cid(it), sec || 'minis');
    return '<button type="button" class="dvh-mini" data-dvh-open="' + (isBlog ? 'blog' : 'item') + '" data-id="' + esc(it.id) + '"><span class="dvh-mini__media">' + picHTML(slotIds(sl), fbMeta(isBlog ? (it.name || (d.byId[it.char] || {}).name || name) : name, isBlog ? 'Blog' : TYPE_LABEL[it.type], ''), { want: 330, alt: '', sizes: '64px', fb: 'mini', slot: sl }) + '</span>' +
      '<span class="dvh-mini__body"><span class="dvh-kicker">' + esc(isBlog ? ((it.cats || [])[0] || 'Blog') : (it.kicker || TYPE_LABEL[it.type])) + '</span><span class="dvh-mini__title">' + esc(name) + '</span></span></button>';
  }

  /* Credits for every image shown in the open panel/article (mini cards and moments included). */
  function imageCredits(A, tag) {
    var U = A.used, keys = Object.keys(U);
    if (!keys.length) return '';
    return '<section class="dvh-block dvh-imgcredits" data-dvh-imgcredits><' + (tag || 'h3') + ' class="dvh-h3">Image credits</' + (tag || 'h3') + '><ul>' + keys.map(function (k) {
      var r = imgRec(U[k].id); if (!r) return '';
      return '<li data-key="' + esc(k) + '">' + esc(r.caption || r.image_alt || 'Image') + ' — <a href="' + esc(r.source_url) + '" target="_blank" rel="noopener">' + esc(r.attribution || 'Wikimedia Commons') + '</a> · ' +
        (r.licenseUrl ? '<a href="' + esc(r.licenseUrl) + '" target="_blank" rel="noopener">' + esc(r.license) + '</a>' : esc(r.license)) + ' · Wikimedia Commons</li>';
    }).join('') + '</ul></section>';
  }
  function openItem(id, opts) {
    var f = fandomCfg(STATE.fandom), d = part(STATE.fandom), it = d && d.byId[id];
    if (!it || it.title) return;
    var s = source(it.src), blog = blogFor(it.id, d);
    LAYER = new Alloc('layer');
    var hs = pickSlot(LAYER, itemCands(it, d), cid(it), 'panel-hero');
    // gallery: the item's other own images not already shown (never the hero again)
    var gal = hs.id && hs.t === 1 ? LAYER.all(ownImgs(it).map(function (x) { return { id: x }; }), cid(it), 'panel-gallery', 8) : [];
    var appears = (d.items || []).filter(function (x) { return x !== it && (x.rel || []).indexOf(it.id) > -1; });
    var linked = (it.rel || []).map(function (r) { return d.byId[r]; }).filter(Boolean);
    var chars = appears.concat(linked).filter(function (x) { return x.type === 'character' || x.type === 'team' || x.type === 'dragon' || x.type === 'driver'; });
    var stories = appears.filter(function (x) { return ['story', 'event', 'moment', 'note'].indexOf(x.type) > -1; });
    var facts = (d.items || []).filter(function (x) { return x.type === 'fact' && (x.src === it.src || (x.rel || []).indexOf(it.id) > -1); });
    var html = '<article class="dvh-panel">' +
      '<div class="dvh-panel__media">' + picHTML(slotIds(hs), fbMeta(it.name, TYPE_LABEL[it.type], it.src), { want: 960, eager: true, alt: hs.p && imgRec(hs.id) ? imgRec(hs.id).image_alt : it.name, sizes: '(max-width: 640px) 100vw, 640px', fb: 'big', slot: hs }) + creditHTML(slotIds(hs), hs.p) + '</div>' +
      '<div class="dvh-panel__body">' +
        '<p class="dvh-kicker">' + esc(f.name) + ' · ' + esc(TYPE_LABEL[it.type]) + (it.year ? ' · ' + esc(it.year) : '') + '</p>' +
        '<h2 class="dvh-panel__title">' + esc(it.name) + '</h2>' +
        (it.kicker ? '<p class="dvh-panel__identity">' + esc(it.kicker) + '</p>' : '') +
        '<p class="dvh-panel__lede">' + esc(it.text) + '</p>' + srcLine(it.src) +
        (blog ? '<button type="button" class="dvh-btn" data-dvh-open="blog" data-id="' + esc(blog.id) + '">Read: ' + esc(blog.title) + ' →</button>' : '') +
        galleryHTML(gal, it.name, 'More images', { cid: cid(it), sec: 'panel-gallery', pool: 'layer', t: 1 }) +
        '<section class="dvh-block" data-dvh-overview><h3 class="dvh-h3">Overview</h3><div class="dvh-skel dvh-skel--text"></div><div class="dvh-skel dvh-skel--text"></div></section>' +
        (s.api === 'wp' && it.wd && it.type === 'character' ? '<section class="dvh-block" data-dvh-wd hidden><h3 class="dvh-h3">Key facts</h3><dl class="dvh-dl"></dl><p class="dvh-src">Data: <a href="https://www.wikidata.org/wiki/' + esc(it.wd) + '" target="_blank" rel="noopener">Wikidata ' + esc(it.wd) + '</a> <span class="dvh-src__lic">CC0</span></p></section>' : '') +
        (stories.length ? '<section class="dvh-block"><h3 class="dvh-h3">Important stories & moments</h3><div class="dvh-minis">' + stories.map(function (x) { return miniCard(x, d, LAYER, 'panel-stories'); }).join('') + '</div></section>' : '') +
        (it.cn ? '<section class="dvh-block" data-dvh-cn hidden><h3 class="dvh-h3">Recent comic stories</h3><ul class="dvh-headlines"></ul><p class="dvh-src">Headlines and links from ComicNewbies — text and images are not reproduced.</p></section>' : '') +
        (facts.length ? '<section class="dvh-block"><h3 class="dvh-h3">Did you know?</h3>' + facts.map(factCard).join('') + '</section>' : '') +
        (chars.length ? '<section class="dvh-block"><h3 class="dvh-h3">Related</h3><div class="dvh-minis">' + chars.map(function (x) { return miniCard(x, d, LAYER, 'panel-related'); }).join('') + '</div></section>' : '') +
        shopBlock(f, [it.name], 'Related DIREVULF products') +
        imageCredits(LAYER) +
        '<p class="dvh-panel__foot">' + (s.url ? ext(s.url, 'Explore on ' + esc(s.name) + ' ↗', 'dvh-link') : '') + '</p>' +
      '</div></article>';
    openLayer(html, 'panel', it.name);
    if (!(opts && opts.fromHistory)) setUrl({ view: 'item:' + it.id }, false);
    // live overview
    var ov = $('[data-dvh-overview]', layer);
    overview(it.src).then(function (r) {
      if (!ov.isConnected) return;
      var o = r.v;
      ov.innerHTML = '<h3 class="dvh-h3">Overview</h3><p class="dvh-quote">' + esc(o.extract) + '</p>' +
        '<p class="dvh-src">From ' + ext(o.url || s.url, esc(s.name) + ' — “' + esc(o.title) + '” ↗', 'dvh-link dvh-link--sm') + ' · <span class="dvh-src__lic">' + esc(s.license) + '</span>' + (r.stale ? ' · saved copy' : '') + '</p>';
    }).catch(function () {
      if (!ov.isConnected) return;
      ov.innerHTML = s.url ? '<h3 class="dvh-h3">Overview</h3>' + notice('The live overview is unavailable right now.', ext(s.url, 'Read it on ' + esc(s.name) + ' ↗', 'dvh-link')) : '';
    });
    var wd = $('[data-dvh-wd]', layer);
    if (wd) wikidataFacts(it.wd).then(function (r) {
      var dl = $('dl', wd), keys = Object.keys(r.v || {}).filter(function (k) { return r.v[k].length; });
      if (!keys.length || !wd.isConnected) return;
      dl.innerHTML = keys.map(function (k) { return '<dt>' + esc(k) + '</dt><dd>' + esc(r.v[k].join(', ')) + '</dd>'; }).join('');
      wd.hidden = false;
    }).catch(function () {});
    var cn = $('[data-dvh-cn]', layer);
    if (cn) comicNewbies(it.cn).then(function (r) {
      var list = (r.v || []).slice(0, 5); if (!list.length || !cn.isConnected) return;
      $('ul', cn).innerHTML = list.map(function (p) { return '<li>' + ext(p.url, esc(p.title) + ' ↗', 'dvh-link') + ' <time datetime="' + esc(p.date) + '">' + esc(fmtDate(new Date(p.date))) + '</time></li>'; }).join('');
      cn.hidden = false;
    }).catch(function () {});
  }

  /* ---------- article (character blog) */
  function para(p) {
    var s = source(p.s);
    return '<p>' + esc(p.t) + (s.url ? ' <a class="dvh-cite" href="' + esc(s.url) + '" target="_blank" rel="noopener" title="' + esc(s.name + (s.title ? ' — ' + s.title : '')) + '">[' + esc(s.api === 'fd' ? 'Fandom' : s.name) + ']</a>' : '') + '</p>';
  }
  function labels(fk, n, fname) {
    var L = (CORE.blog_labels || {}), base = L['default'] || {}, over = L[fk] || {}, out = {};
    Object.keys(base).concat(Object.keys(over)).forEach(function (k) { out[k] = String(over[k] || base[k] || '').replace('{n}', n).replace('{f}', fname); });
    return out;
  }
  function openBlog(id, opts) {
    var f = fandomCfg(STATE.fandom), d = part(STATE.fandom), b = d && d.byId[id];
    if (!b || !b.title) return;
    var it = d.byId[b.char] || {}, name = b.name || it.name, L = labels(f.key, name, f.name);
    var srcs = {};
    function collect(o) { if (!o) return; if (Array.isArray(o)) o.forEach(collect); else if (typeof o === 'object') { if (o.s) srcs[o.s] = 1; Object.keys(o).forEach(function (k) { if (typeof o[k] === 'object') collect(o[k]); }); } }
    collect(b);
    var readMin = Math.max(2, Math.round(blogWords(b) / 200));
    // One allocator for the whole article: hero, inline figures in reading order, moments, then
    // the gallery (only images not shown above), then mini cards. No image appears twice.
    LAYER = new Alloc('layer');
    var bc = blogCands(b, d), bid = cid(b);
    var hs = pickSlot(LAYER, bc, bid, 'article-hero'); hs.alts = [];
    var inl = {};
    ['intro', 'who', 'origin', 'evolution', 'personality', 'abilities'].forEach(function (k) {
      var id = (b.inline || {})[k];
      if (!id || !imgRec(id) || !LAYER.free(id) || recBad(id) || (k !== 'intro' && !(b[k] || []).length)) return;
      LAYER.take(id, bid, 'article-inline'); inl[k] = id;
    });
    function inlineFig(key) { var id = inl[key]; if (!id) return ''; return figureHTML(id, fbMeta(name, 'Blog', it.src), { cls: 'dvh-fig--inline', slot: { cid: bid, sec: 'article-inline', pool: 'layer', t: 1 } }); }
    var block = function (key, title, arr) { return arr && arr.length ? '<section class="dvh-block dvh-art__sec" id="dvh-a-' + key + '"><h2 class="dvh-h3">' + esc(title) + '</h2>' + arr.map(para).join('') + inlineFig(key) + '</section>' : ''; };
    var stories = (b.stories || []).concat(b.cars || []).map(function (x) { return d.byId[x]; }).filter(Boolean);
    var moments = (b.moments || []).map(function (m) {
      var ref = m.ref && d.byId[m.ref], s = source(m.s), ms = pickSlot(LAYER, ref ? itemCands(ref, d) : (imgRec(m.s) ? [{ id: m.s, t: 2, p: '' }] : []), ref ? cid(ref) : bid + '-moment', 'article-moments');
      return '<article class="dvh-moment dvh-reveal"><div class="dvh-moment__media">' + picHTML(slotIds(ms), fbMeta(m.title, 'Moment', m.s), { want: 330, alt: '', sizes: '(max-width: 640px) 96px, 140px', fb: 'mini', slot: ms }) + '</div><div><p class="dvh-moment__title">' + esc(m.title) + '</p><p class="dvh-card__text">' + esc(m.t) + '</p>' +
        (ms.id ? '<p class="dvh-credit dvh-credit--inline" data-dvh-credit data-pictured="' + esc(ms.p || '') + '">' + creditInner(ms.id, ms.p) + '</p>' : '') +
        '<p class="dvh-src">Source: ' + (s.url ? '<a href="' + esc(s.url) + '" target="_blank" rel="noopener">' + esc(s.name) + '</a>' : 'DIREVULF') + '</p>' + (ref ? '<button type="button" class="dvh-link dvh-link--sm" data-dvh-open="item" data-id="' + esc(ref.id) + '">Explore →</button>' : '') + '</div></article>';
    }).join('');
    // gallery after the moments (a moment's own image outranks an extra gallery picture)
    var galIds = LAYER.all(bc, bid, 'article-gallery', 8);
    var rel = b.rel || {}, relGroups = [['allies', 'Allies'], ['rivals', 'Rivals'], ['teams', 'Teams'], ['family', 'Family'], ['mentors', 'Mentors']].filter(function (g) { return (rel[g[0]] || []).length; });
    var relHTML = relGroups.length ? '<section class="dvh-block dvh-art__sec" id="dvh-a-rel"><h2 class="dvh-h3">' + esc(L.rel) + '</h2><div class="dvh-rel">' + relGroups.map(function (g) {
      return '<div class="dvh-rel__group"><p class="dvh-kicker">' + g[1] + '</p><div class="dvh-minis">' + rel[g[0]].map(function (r) {
        if (r.id && d.byId[r.id]) { var rb = blogFor(r.id, d); return rb ? miniCard(rb, d, LAYER, 'article-rel') : miniCard(d.byId[r.id], d, LAYER, 'article-rel'); }
        return '<span class="dvh-mini dvh-mini--text"><span class="dvh-mini__media">' + fallbackCard({ n: r.name }, 'mini') + '</span><span class="dvh-mini__body"><span class="dvh-mini__title">' + esc(r.name) + '</span></span></span>';
      }).join('') + '</div></div>';
    }).join('') + '</div>' + srcLine(rel.s) + '</section>' : '';
    var more = (d.blogs || []).filter(function (x) { return x.id !== b.id; }).slice(0, 4).map(function (x) { return miniCard(x, d, LAYER, 'article-more'); }).join('') +
      (d.items || []).filter(function (x) { return ['story', 'event', 'moment', 'team', 'note', 'dragon', 'location', 'car'].indexOf(x.type) > -1 && (x.rel || []).indexOf(b.char) < 0; }).slice(0, 4).map(function (x) { return miniCard(x, d, LAYER, 'article-more'); }).join('');
    var html = '<article class="dvh-art">' +
      '<header class="dvh-article__hero">' + picHTML(slotIds(hs), fbMeta(name, (b.cats || [])[0] || 'Blog', it.src), { want: 1280, eager: true, alt: (imgRec(hs.id) || {}).image_alt || name, sizes: '100vw', fb: 'big', slot: hs }) + '<div class="dvh-article__shade"></div>' +
        '<div class="dvh-article__head"><p class="dvh-kicker">' + esc(f.name) + ' · ' + esc((b.cats || []).join(' · ')) + '</p><h1 class="dvh-article__title">' + esc(b.title) + '</h1><p class="dvh-article__dek">' + esc(b.excerpt) + '</p>' +
        '<p class="dvh-article__meta">' + readMin + ' min read · Published & updated ' + esc(fmtDate(new Date(CORE.updated + 'T00:00:00Z'))) + ' · ' + Object.keys(srcs).length + ' sources</p></div></header>' +
      (hs.id ? '<p class="dvh-credit dvh-art__herocap" data-dvh-credit>' + (imgRec(hs.id).caption ? esc(imgRec(hs.id).caption) + ' · ' : '') + creditInner(hs.id) + '</p>' : '') +
      '<div class="dvh-art__body">' +
        '<nav class="dvh-art__toc" aria-label="In this article">' + [['who', L.who], ['origin', L.origin], ['evolution', L.evolution], ['abilities', L.abilities], ['stories', L.stories], ['moments', L.moments], ['timeline', L.timeline], ['rel', L.rel]].filter(function (x) { return (b[x[0]] && b[x[0]].length) || (x[0] === 'rel' && relGroups.length); }).map(function (x) { return '<a class="dvh-chip" href="#dvh-a-' + x[0] + '">' + esc(x[1]) + '</a>'; }).join('') + '</nav>' +
        '<div class="dvh-art__intro">' + (b.intro || []).map(para).join('') + inlineFig('intro') + '</div>' +
        block('who', L.who, b.who) + block('origin', L.origin, b.origin) + block('evolution', L.evolution, b.evolution) + block('personality', L.personality, b.personality) + block('abilities', L.abilities, b.abilities) +
        galleryHTML(galIds, name, 'Gallery', { cid: bid, sec: 'article-gallery', pool: 'layer', t: 1 }) +
        (stories.length ? '<section class="dvh-block dvh-art__sec" id="dvh-a-stories"><h2 class="dvh-h3">' + esc(L.stories) + '</h2><div class="dvh-minis dvh-minis--wide">' + stories.map(function (x) { return miniCard(x, d, LAYER, 'article-stories'); }).join('') + '</div></section>' : '') +
        (moments ? '<section class="dvh-block dvh-art__sec" id="dvh-a-moments"><h2 class="dvh-h3">' + esc(L.moments) + '</h2><div class="dvh-moments">' + moments + '</div></section>' : '') +
        ((b.facts || []).length ? '<section class="dvh-block dvh-art__sec"><h2 class="dvh-h3">' + esc(L.facts) + '</h2><ul class="dvh-facts">' + b.facts.map(function (x) { var s = source(x.s); return '<li><span>' + esc(x.t) + '</span> ' + (s.url ? '<a class="dvh-cite" href="' + esc(s.url) + '" target="_blank" rel="noopener">[' + esc(s.api === 'fd' ? 'Fandom' : s.name) + ']</a>' : '') + '</li>'; }).join('') + '</ul></section>' : '') +
        (b.dyk ? '<aside class="dvh-dyk"><p class="dvh-fact__label">Did you know?</p><p class="dvh-fact__text">' + esc(b.dyk.t) + '</p>' + srcLine(b.dyk.s) + '</aside>' : '') +
        ((b.timeline || []).length ? '<section class="dvh-block dvh-art__sec" id="dvh-a-timeline"><h2 class="dvh-h3">' + esc(L.timeline) + '</h2><ol class="dvh-vtl">' + b.timeline.map(function (t) { var s = source(t.s); return '<li><span class="dvh-vtl__y">' + esc(t.y) + '</span><span class="dvh-vtl__t">' + esc(t.t) + (s.url ? ' <a class="dvh-cite" href="' + esc(s.url) + '" target="_blank" rel="noopener">[' + esc(s.api === 'fd' ? 'Fandom' : s.name) + ']</a>' : '') + '</span></li>'; }).join('') + '</ol></section>' : '') +
        relHTML +
        (more ? '<section class="dvh-block dvh-art__sec"><h2 class="dvh-h3">' + esc(L.more) + '</h2><div class="dvh-minis dvh-minis--wide">' + more + '</div></section>' : '') +
        shopBlock(f, [name], L.shop) +
        '<section class="dvh-block dvh-art__sources"><h2 class="dvh-h3">Sources & licences</h2><ul>' + Object.keys(srcs).map(function (c) { var s = source(c); return s.url ? '<li>' + ext(s.url, esc(s.name) + (s.title ? ' — ' + esc(s.title) : '') + ' ↗', 'dvh-link') + ' · ' + esc(s.license) + '</li>' : ''; }).join('') + '</ul>' +
        '<p class="dvh-src">DIREVULF editorial summary written from the sources above (retrieved ' + esc(CORE.retrieved) + '). Not affiliated with the rights holders.</p></section>' +
        imageCredits(LAYER, 'h2') +
      '</div></article>';
    openLayer(html, 'article', b.title);
    if (!(opts && opts.fromHistory)) setUrl({ view: 'blog:' + b.id }, false);
  }

  /* ------------------------------------------------------------------ hub search (separate from the header search) */
  var sInput = $('[data-dvh-search]'), sBox = $('[data-dvh-results]'), sTimer = null, sSel = -1, sHits = [];
  function allParts() { return Promise.all((CORE.fandoms || []).map(function (f) { return loadPart(f.key).catch(function () { return null; }); })); }
  function search(q) {
    var nq = norm(q); if (nq.length < 2) { closeResults(); return; }
    var terms = nq.split(' ');
    allParts().then(function () {
      var hits = [];
      (CORE.fandoms || []).forEach(function (f) {
        var d = part(f.key); if (!d) return;
        (d.items || []).forEach(function (i) {
          var name = norm(i.name), hay = name + ' ' + norm(i.kicker) + ' ' + norm(i.text);
          if (!terms.every(function (t) { return hay.indexOf(t) > -1; })) return;
          var score = (name.indexOf(nq) === 0 ? 50 : name.indexOf(nq) > -1 ? 30 : 0) + (i.type === 'character' || i.type === 'driver' ? 10 : 0) + (i.type === 'fact' ? -5 : 0);
          hits.push({ f: f, kind: 'item', id: i.id, title: i.type === 'fact' ? i.text : i.name, sub: (TYPE_LABEL[i.type] || '') + ' · ' + (f.short || f.label), score: score });
        });
        (d.blogs || []).forEach(function (b) {
          var hay = norm(b.title + ' ' + b.excerpt + ' ' + (b.name || '') + ' ' + ((d.byId[b.char] || {}).name || ''));
          if (terms.every(function (t) { return hay.indexOf(t) > -1; })) hits.push({ f: f, kind: 'blog', id: b.id, title: b.title, sub: 'Blog · ' + (f.short || f.label), score: 25 });
        });
        var col = (CFG.collections || {})[f.collection];
        ((col && col.products) || []).forEach(function (p) {
          var hay = norm(p.t + ' ' + p.tags + ' ' + p.type);
          if (terms.every(function (t) { return hay.indexOf(t) > -1; })) hits.push({ f: f, kind: 'product', url: p.u, title: p.t, sub: 'Product · ' + p.p, score: 5 });
        });
        if (terms.every(function (t) { return norm(f.name + ' ' + f.label).indexOf(t) > -1; })) hits.push({ f: f, kind: 'fandom', title: 'Explore ' + f.name, sub: 'Fandom', score: 60 });
      });
      hits.sort(function (a, b) { return b.score - a.score; });
      sHits = hits.slice(0, 12); sSel = -1;
      var extLinks = '<div class="dvh-results__ext">Not here? ' + ext('https://en.wikipedia.org/w/index.php?search=' + encodeURIComponent(q), 'Search Wikipedia ↗', 'dvh-link dvh-link--sm') + '</div>';
      sBox.innerHTML = (sHits.length ? sHits.map(function (h, i) {
        return '<button type="button" role="option" class="dvh-results__row" id="dvh-r-' + i + '" data-i="' + i + '"><span class="dvh-results__title">' + esc(h.title) + '</span><span class="dvh-results__sub">' + esc(h.sub) + '</span></button>';
      }).join('') : '<p class="dvh-results__none">Nothing in the hub matches “' + esc(q) + '”.</p>') + extLinks;
      sBox.hidden = false; sInput.setAttribute('aria-expanded', 'true');
    });
  }
  function closeResults() { if (sBox) { sBox.hidden = true; sInput.setAttribute('aria-expanded', 'false'); sInput.removeAttribute('aria-activedescendant'); } }
  function pickHit(h) {
    closeResults();
    if (!h) return;
    if (h.kind === 'product') { location.href = h.url; return; }
    setFandom(h.f.key, { push: false }).then(function () {
      if (h.kind === 'item') openItem(h.id);
      else if (h.kind === 'blog') openBlog(h.id);
      else document.getElementById('dvh-content').scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth' });
    });
  }
  if (sInput) {
    sInput.addEventListener('input', function () { clearTimeout(sTimer); var v = sInput.value; sTimer = setTimeout(function () { search(v); }, 160); });
    sInput.addEventListener('keydown', function (e) {
      if (sBox.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault(); sSel = Math.max(0, Math.min(sHits.length - 1, sSel + (e.key === 'ArrowDown' ? 1 : -1)));
        $$('.dvh-results__row', sBox).forEach(function (r, i) { r.classList.toggle('is-sel', i === sSel); });
        sInput.setAttribute('aria-activedescendant', 'dvh-r-' + sSel);
      } else if (e.key === 'Enter') { e.preventDefault(); pickHit(sHits[sSel > -1 ? sSel : 0]); }
      else if (e.key === 'Escape') { closeResults(); }
    });
    sBox.addEventListener('click', function (e) { var r = e.target.closest('.dvh-results__row'); if (r) pickHit(sHits[+r.getAttribute('data-i')]); });
    root.addEventListener('focusout', function (e) { if (!e.relatedTarget || !root.querySelector('[data-dvh-search-form]').contains(e.relatedTarget)) setTimeout(closeResults, 150); });
  }

  /* ------------------------------------------------------------------ URL state */
  function params() { return new URLSearchParams(location.search); }
  function setUrl(o, replace) {
    try {
      var p = params();
      if ('fandom' in o) { if (o.fandom) p.set('fandom', o.fandom); else p.delete('fandom'); }
      if ('view' in o) { if (o.view) p.set('dvh', o.view); else p.delete('dvh'); }
      var url = location.pathname + (p.toString() ? '?' + p.toString() : '') + location.hash;
      var st = { dvh: 1, dvhv: p.get('dvh') || null };
      if (replace) history.replaceState(st, '', url); else history.pushState(st, '', url);
    } catch (e) {}
  }
  window.addEventListener('popstate', function () {
    var p = params(), fk = p.get('fandom'), v = p.get('dvh');
    var go = fk && fk !== STATE.fandom ? setFandom(fk, { push: false, keepView: true }) : Promise.resolve();
    go.then(function () {
      if (!v) { closeLayer(true); return; }
      var bits = v.split(':');
      if (bits[0] === 'item') openItem(bits[1], { fromHistory: true }); else if (bits[0] === 'blog') openBlog(bits[1], { fromHistory: true });
    });
  });

  /* ------------------------------------------------------------------ switching fandom */
  var switching = 0;
  function setFandom(key, opts) {
    opts = opts || {};
    var f = fandomCfg(key) || (CORE.fandoms || [])[0];
    if (!f) return Promise.resolve();
    var token = ++switching;
    if (STATE.fandom !== f.key) { STATE.franchise = 'all'; STATE.blogCat = 'all'; }
    STATE.fandom = f.key;
    if (!opts.keepView) setUrl({ fandom: f.key, view: null }, true);
    var body = $('[data-dvh-body]');
    body.setAttribute('aria-busy', 'true');
    body.classList.add('is-switching');
    return loadPart(f.key).then(function (d) {
      if (token !== switching) return;
      renderHero(f, d); renderSubnav(f, d); renderBody(f, d);
      body.classList.remove('is-switching'); body.removeAttribute('aria-busy');
    }).catch(function () {
      if (token !== switching) return;
      body.classList.remove('is-switching'); body.removeAttribute('aria-busy');
      var col = (CFG.collections || {})[f.collection];
      body.innerHTML = '<div class="dvh-wrap">' + notice('This fandom’s stories couldn’t load right now. Please refresh in a moment.', col ? '<a class="dvh-btn dvh-btn--ghost" href="' + esc(col.url) + '">Shop ' + esc(f.name) + ' →</a>' : '') + '</div>';
    });
  }

  /* ------------------------------------------------------------------ events (delegated) */
  root.addEventListener('click', function (e) {
    var t = e.target;
    var gg = t.closest('[data-dvh-gal-go]');
    if (gg) { var gal = gg.closest('[data-dvh-gal]'), tr = $('.dvh-gal__track', gal), gi = +gg.getAttribute('data-dvh-gal-go'); tr.scrollTo({ left: gi * tr.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' }); galActive(gal, gi); return; }
    var dp = t.closest('[data-dvh-dept]');
    if (dp) { shopDept(dp.getAttribute('data-dvh-dept')); return; }
    if (t.closest('[data-dvh-shop-more]')) { shopMore(); return; }
    var fb = t.closest('[data-dvh-fandom]');
    if (fb) { setFandom(fb.getAttribute('data-dvh-fandom')); return; }
    if (t.closest('[data-dvh-close]')) { closeLayer(); return; }
    var fr = t.closest('[data-dvh-franchise]');
    if (fr) { STATE.franchise = fr.getAttribute('data-dvh-franchise'); STATE.blogCat = 'all'; var f = fandomCfg(STATE.fandom), d = part(STATE.fandom); renderSubnav(f, d); renderBody(f, d); return; }
    var bc = t.closest('[data-dvh-blogcat]');
    if (bc) {
      STATE.blogCat = bc.getAttribute('data-dvh-blogcat');
      var f2 = fandomCfg(STATE.fandom), d2 = part(STATE.fandom), sec = (f2.sections || []).filter(function (s) { return s.type === 'blogs'; })[0];
      var wrap = $('#dvh-sec-blogs'); if (wrap && sec) { PAGE.releaseSec('blogs'); Object.keys(PLAN).forEach(function (k) { if (k.indexOf('blogs:') === 0) delete PLAN[k]; }); var tmp = document.createElement('div'); tmp.innerHTML = RENDER.blogs(sec, f2, d2); wrap.parentNode.replaceChild(tmp.firstChild, wrap); reveal(); var btn = $('[data-dvh-blogcat="' + CSS.escape(STATE.blogCat) + '"]'); if (btn) btn.focus({ preventScroll: true }); }
      return;
    }
    if (t.closest('[data-dvh-explore]')) { e.preventDefault(); document.getElementById('dvh-content').scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth' }); return; }
    if (t.closest('a[href], .dvh-link:not([data-dvh-open])') && !t.closest('[data-dvh-open]')) return;
    var op = t.closest('[data-dvh-open]');
    if (op && !t.closest('a[href]')) {
      var kind = op.getAttribute('data-dvh-open'), id = op.getAttribute('data-id');
      if (kind === 'item') openItem(id); else if (kind === 'blog') openBlog(id);
    }
  });
  root.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches && e.target.matches('[data-dvh-open][role="button"]')) { e.preventDefault(); e.target.click(); }
    var tab = e.target.closest && e.target.closest('[data-dvh-fandom]');
    if (tab && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      var tabs = $$('[data-dvh-fandom]'), i = tabs.indexOf(tab), n = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      n.focus(); n.click();
    }
  });

  /* ------------------------------------------------------------------ boot */
  loadCore().then(function () {
    renderSelector();
    var p = params(), want = p.get('fandom') || CFG.defaultFandom || 'marvel';
    if (!fandomCfg(want)) want = (CORE.fandoms[0] || {}).key;
    return setFandom(want, { keepView: true }).then(function () {
      var v = p.get('dvh');
      if (v) { var b = v.split(':'); if (b[0] === 'item') openItem(b[1], { fromHistory: true }); else if (b[0] === 'blog') openBlog(b[1], { fromHistory: true }); }
    });
  }).catch(function () {
    $('[data-dvh-selector]').innerHTML = '';
    $('[data-dvh-body]').innerHTML = '<div class="dvh-wrap">' + notice('The fandom hub couldn’t load right now. Please refresh in a moment — or shop the fandoms directly.',
      Object.keys(CFG.collections || {}).map(function (h) { var c = CFG.collections[h]; return c ? '<a class="dvh-btn dvh-btn--ghost" href="' + esc(c.url) + '">' + esc(c.title) + '</a>' : ''; }).join(' ')) + '</div>';
  });

  /* Image audit for QA: every image slot in the page (or the open panel/article), what it shows,
     and any image shown more than once. DVFandomHub.audit('page' | 'layer'). */
  function hasCredit(x) {
    if (x.closest('[data-dvh-collage]')) return true;
    if (x.closest('.dvh-article__hero')) return !!$('.dvh-art__herocap', layer);
    var box = x.closest('[data-dvh-fig], .dvh-card, .dvh-panel__media, .dvh-moment, .dvh-gal__slide, .dvh-mini');
    if (box && box.classList.contains('dvh-mini')) return !!layer.querySelector('[data-dvh-imgcredits] li[data-key="' + imgKey(x.getAttribute('data-cur')).replace(/"/g, '') + '"]');
    return !!(box && box.querySelector('[data-dvh-credit]'));
  }
  function audit(scope) {
    var host = scope === 'layer' ? layer : root, rows = [];
    $$('[data-cid]', host).forEach(function (x) {
      if (scope !== 'layer' && layer.contains(x)) return;
      if (x.hasAttribute('data-nav')) return;
      var isImg = x.tagName === 'IMG', id = isImg ? x.getAttribute('data-cur') : null;
      rows.push({ cid: x.getAttribute('data-cid'), sec: x.getAttribute('data-sec'), tier: +x.getAttribute('data-tier') || 0, img: id, key: id ? imgKey(id) : null,
        state: !isImg ? 'fallback' : x.naturalWidth > 0 ? 'ok' : 'pending', w: isImg ? x.naturalWidth : 0, src: isImg ? (x.currentSrc || x.src) : '',
        alt: isImg ? x.getAttribute('alt') : null, credit: isImg ? hasCredit(x) : null, failed: !isImg && x.getAttribute('data-failed') === '1' });
    });
    var byKey = {};
    rows.forEach(function (r) { if (r.key) (byKey[r.key] = byKey[r.key] || []).push(r.cid + ' [' + r.sec + ']'); });
    var dups = Object.keys(byKey).filter(function (k) { return byKey[k].length > 1; }).map(function (k) { return { image: k, count: byKey[k].length, items: byKey[k] }; }).sort(function (a, b) { return b.count - a.count; });
    return { scope: scope || 'page', fandom: STATE.fandom, slots: rows.length, withImage: rows.filter(function (r) { return r.img; }).length,
      fallback: rows.filter(function (r) { return !r.img; }).length, failedToCard: rows.filter(function (r) { return r.failed; }).length, pending: rows.filter(function (r) { return r.state === 'pending'; }).length,
      byTier: rows.reduce(function (a, r) { var k = r.img ? 'T' + r.tier : 'text-card'; a[k] = (a[k] || 0) + 1; return a; }, {}),
      duplicates: dups, rows: rows };
  }
  window.DVFandomHub = { version: 4, open: function (k) { return setFandom(k); }, openItem: function (id) { openItem(id); }, openBlog: function (id) { openBlog(id); }, audit: audit,
    usage: function (scope) { var U = allocFor(scope).used; return Object.keys(U).map(function (k) { return { image: U[k].id, uses: U[k].n, by: U[k].owners.slice(), section: U[k].sec }; }); } };
})();